import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Calculator,
  Plus,
  Trash2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Package,
  User,
  Wrench,
  Settings,
  Pencil,
  Printer,
  FileDown,
  Building2,
  Copy,
  MoreVertical,
  ArrowRight,
  Sparkles,
  FileSpreadsheet,
  Layers,
  BarChart3,
} from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import AnalysisInsights from './AnalysisInsights';
import {
  projectAnalysisApi,
  type AnalysisItem,
  type AnalysisSection,
  type AnalysisSectionItem,
  type PrintData,
  type ProjectOption,
} from '../lib/api/projectAnalysis';
import { getBackendBaseUrl } from '../lib/api/config';
import LoadingSpinner from './common/LoadingSpinner';
import AnalysisAiPanel from './AnalysisAiPanel';
import ConfirmModal from './common/ConfirmModal';
import Modal from './common/Modal';
import SearchableSelect, { type SearchableSelectOption } from './common/SearchableSelect';
import {
  sectionInsuranceAmount,
  sectionInsuranceRate,
  effectiveInsurancePctOnTotal,
  analysisInsuranceMode,
  verifyTotalsSum,
  profitTypeLabel,
  parseAmount,
} from '../lib/analysisCalculations';
import {
  formatMoney,
  formatCompactMoney,
  formatQuantity,
  formatPercentDisplay,
  formatNumberInput,
  formatMoneyInput,
  parseNumberInput,
  parseMoneyInput,
} from '../lib/numberFormat';

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

/** مبالغ ریالی — بدون اعشار */
const formatNumber = formatMoney;
/** درصدها — حداکثر ۲ رقم اعشار */
const formatPercent = (n: string | number | null | undefined) => formatPercentDisplay(n, 2);

const getItemTypeIcon = (name: string) => {
  const n = name.toLowerCase();
  if (n.includes('کارگر') || n.includes('بنای') || n.includes('استاد')) return User;
  if (n.includes('سیمان') || n.includes('ماسه') || n.includes('شن')) return Package;
  return Wrench;
};

const PROFIT_TYPE_OPTIONS = [
  { value: 'percentage_gross', label: 'درصد از بهای پایه (ناخالص)' },
  { value: 'percentage_net', label: 'درصد از خالص (بعد از بیمه و مالیات)' },
  { value: 'amount', label: 'مبلغ ثابت' },
];

const CHART_COLORS = ['#0f766e', '#0369a1', '#7c3aed', '#d97706'];

type PrintOptions = {
  includeCharts: boolean;
  signatures?: Array<{ position: string; name: string }>;
  fontSize?: number;
  /** رقم اعشار برای مبالغ (بهای واحد، قیمت کل، جمع‌ها، خلاصه مالی) */
  decimalPlacesAmounts?: number;
  /** رقم اعشار برای مقادیر (تعداد، مقدار) */
  decimalPlacesQuantity?: number;
  /** رقم اعشار برای ضریب پیمانکار */
  decimalPlacesCoefficient?: number;
  /** نمایش تعدیل و مبلغ با تعدیل در خروجی */
  showAdjustmentInPrint?: boolean;
  /** اعمال تعدیل در مبالغ نهایی خروجی (قیمت نهایی = با تعدیل). اگر false باشد قیمت به کارفرما بدون تعدیل است. */
  includeAdjustmentInPrint?: boolean;
  /** نمایش اقساط پیش‌پرداخت (مبالغ قسط‌ها) در خروجی */
  includePrepaymentInPrint?: boolean;
};

/** چارت میله‌ای جدولی - برای چاپ و PDF قابل اطمینان‌تر از SVG */
const buildChartTableHtml = (
  data: { name: string; value: number; color: string }[],
  fmt: (n: number) => string
): string => {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total <= 0 || data.length === 0) return '';
  const rows = data
    .map(
      (d) => {
        const pct = (d.value / total) * 100;
        const w = Math.max(5, Math.round(pct));
        return `<tr>
          <td style="width:22%;font-weight:bold;text-align:right;padding:4px 6px;border:1px solid #444">${d.name}</td>
          <td style="width:48%;padding:2px 4px;border:1px solid #444;vertical-align:middle;direction:ltr;text-align:left">
            <div style="height:14px;background:${d.color};width:${w}%;min-width:3px;border-radius:2px;display:inline-block"></div>
          </td>
          <td style="width:15%;text-align:left;font-family:monospace;padding:4px 6px;border:1px solid #444">${pct.toFixed(1)}٪</td>
          <td style="width:15%;text-align:left;font-family:monospace;font-weight:bold;padding:4px 6px;border:1px solid #444">${fmt(d.value)}</td>
        </tr>`;
      }
    )
    .join('');
  return `<div class="chart-print-block" style="margin:12px 0 16px 0;page-break-inside:avoid">
    <p style="margin:0 0 6px 0;font-weight:bold;font-size:11px;text-align:center">توزیع هزینه‌ها</p>
    <table style="width:100%;max-width:420px;margin:0 auto;border-collapse:collapse;font-size:10px;background:#fff">
      <thead><tr style="background:#e0e0e0;font-weight:bold">
        <th style="text-align:right;padding:6px;border:1px solid #333">عنوان</th>
        <th style="text-align:center;padding:6px;border:1px solid #333">سهم</th>
        <th style="text-align:center;padding:6px;border:1px solid #333">درصد</th>
        <th style="text-align:left;padding:6px;border:1px solid #333">مبلغ</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
  </div>`;
};

const buildPrintHtmlWysiwyg = (data: PrintData, options: PrintOptions = { includeCharts: true }): string => {
  const decimalsAmount = options.decimalPlacesAmounts ?? 0;
  const decimalsQty = options.decimalPlacesQuantity ?? 2;
  const decimalsCoef = options.decimalPlacesCoefficient ?? 4;
  const formatWithDecimals = (n: string | number | null | undefined, decimals: number) => {
    if (n == null || n === '') return '۰';
    const num = typeof n === 'string' ? parseFloat(String(n).replace(/[,٬\s]/g, '')) : n;
    if (isNaN(num)) return '۰';
    const rounded = decimals >= 0 ? Number(num.toFixed(decimals)) : num;
    const [intPart, decPart] = rounded.toString().split('.');
    const withSeparator = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '٬');
    const toPersian = (s: string) => s.replace(/\d/g, (d) => PERSIAN_DIGITS[parseInt(d, 10)]);
    return decPart ? `${toPersian(withSeparator)}.${toPersian(decPart)}` : toPersian(withSeparator);
  };
  const fmt = (n: string | number | null | undefined) => formatWithDecimals(n, decimalsAmount);
  const fmtQty = (n: string | number | null | undefined) => formatWithDecimals(n, decimalsQty);
  const fmtCoef = (n: string | number | null | undefined) => formatWithDecimals(n, decimalsCoef);
  const ca = data.cost_analysis;
  const proj = data.project;
  const client = data.client;
  const company = data.company;
  const backendUrl = getBackendBaseUrl() || '';

  const sectionsHtml = (ca.sections ?? [])
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(
      (s, idx) => `
      <div class="section-block">
        <h3 class="section-title">${idx + 1}. ${s.name}</h3>
        ${s.description ? `<p class="section-desc">${s.description}</p>` : ''}
        <table class="cost-table">
          <thead><tr>
            <th class="col-row">ردیف</th>
            <th class="col-desc">شرح کار</th>
            <th class="col-qty">تعداد</th>
            <th class="col-unit">واحد</th>
            <th class="col-unit-price">قیمت واحد</th>
            <th class="col-total">قیمت کل</th>
          </tr></thead>
          <tbody>
            ${(s.items ?? []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((i, iIdx) => `
              <tr><td class="text-center">${iIdx + 1}</td><td class="text-right">${i.name}</td><td class="text-center">${fmtQty(i.quantity)}</td>
              <td class="text-center">${i.unit ?? '—'}</td><td class="text-left">${fmt(i.unit_cost)}</td><td class="text-left font-bold">${fmt(i.total_cost)}</td></tr>
            `).join('')}
          </tbody>
          <tfoot>
            <tr class="total-row"><td colspan="5" class="text-left font-bold">جمع پایه</td>
            <td class="text-left font-bold">${fmt(s.base_cost)}</td></tr>
            ${(() => {
              const insPct = sectionInsuranceRate(s, ca);
              if (insPct <= 0) return '';
              const insAmt = sectionInsuranceAmount(s, ca);
              return `<tr class="total-row"><td colspan="5" class="text-left font-bold">بیمه (${insPct}٪ از جمع کل بخش)</td>
            <td class="text-left font-bold">${fmt(insAmt)}</td></tr>`;
            })()}
            <tr class="total-row section-grand"><td colspan="5" class="text-left font-bold">جمع کل بخش</td>
          <td class="text-left font-bold">${fmt(s.calculated_cost)}</td></tr></tfoot>
        </table>
      </div>`
    )
    .join('');

  const clientLogo = client.logo ? `<img src="${client.logo}" alt="کارفرما" class="logo">` : '<div class="logo-placeholder">کارفرما</div>';
  const companyLogo = company.company_logo ? `<img src="${company.company_logo}" alt="پیمانکار" class="logo">` : '<div class="logo-placeholder">پیمانکار</div>';

  return `<!DOCTYPE html>
<html dir="rtl" lang="fa">
<head>
  <meta charset="UTF-8">
  <title>گزارش آنالیز بها - ${ca.name}</title>
  <link href="https://fonts.googleapis.com/css2?family=Vazirmatn:wght@300;400;500;600;700;800&display=swap" rel="stylesheet">
  <style>
    :root{--font-size:11px;--cell-pad:2px;--page-orient:landscape;--page-margin:10mm}
    *{margin:0;padding:0;box-sizing:border-box}
    html,body{font-family:'Vazirmatn',Tahoma,sans-serif;font-size:var(--font-size);color:#000;background:#525659}
    body{padding:15px}
    .no-print{display:block}
    .print-panel{background:#fff;padding:12px 15px;border-bottom:4px solid #0d9488;box-shadow:0 2px 10px rgba(0,0,0,0.2);margin-bottom:15px;display:flex;flex-wrap:wrap;gap:12px;align-items:center;font-size:12px}
    .print-panel .ctl{display:flex;flex-direction:column;gap:4px}
    .print-panel label{font-weight:bold;color:#334}
    .print-panel input[type=range],.print-panel select,.print-panel input[type=number]{font-size:11px}
    .print-panel .btn{background:#0d9488;color:#fff;border:none;padding:8px 16px;border-radius:6px;cursor:pointer;font-weight:bold;font-family:inherit}
    .print-panel .btn:hover{background:#0f766e}
    .print-content{background:#fff;padding:15px;margin:0 auto;box-shadow:0 0 15px rgba(0,0,0,0.4);transition:max-width 0.25s ease}
    body.orient-landscape .print-content{max-width:297mm}
    body.orient-portrait .print-content{max-width:210mm}
    .header-area{border:2px solid #0a4d29;border-radius:5px;padding:8px;margin-bottom:10px;page-break-after:avoid}
    .header-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px}
    .logo-area{width:100px;height:70px;display:flex;flex-direction:column;align-items:center;justify-content:center}
    .logo{max-width:90px;max-height:60px;object-fit:contain}
    .logo-placeholder{width:90px;height:60px;border:2px dashed #999;display:flex;align-items:center;justify-content:center;font-size:9px;color:#666}
    .title-area{text-align:center;flex:1}
    .main-title{font-size:16px;font-weight:bold;margin-bottom:5px}
    .project-title{font-size:12px;color:#333}
    .info-table{width:100%;border-collapse:collapse;font-size:var(--font-size)}
    .info-table td{padding:4px 6px;border:1px solid #333}
    .info-label{background:#f0f0f0;font-weight:bold;width:18%}
    .section-block{margin-bottom:14px;page-break-inside:auto}
    .section-title{font-size:12px;font-weight:bold;margin:0 0 4px 0;padding:4px 6px;border-bottom:2px solid #0a4d29;background:#ecfdf5;page-break-after:avoid}
    .section-desc{font-style:italic;font-size:9px;color:#666;margin-bottom:4px}
    table{width:100%;border-collapse:collapse;font-size:var(--font-size);page-break-inside:auto}
    .cost-table thead{display:table-header-group}
    .cost-table tr{page-break-inside:avoid}
    th,td{border:1px solid #444;padding:var(--cell-pad) 4px;text-align:center}
    th{background:#d0d0d0;font-weight:bold;border:2px solid #000}
    .col-row{width:5%}.col-desc{width:38%}.col-qty{width:10%}.col-unit{width:10%}.col-unit-price{width:17%}.col-total{width:20%}
    .text-right{text-align:right}.text-left{text-align:left}.text-center{text-align:center}
    .total-row{background:#e8e8e8;font-weight:bold}
    .section-grand{background:#d1fae5}
    .summary-section{margin-top:12px;border:2px solid #0a4d29;padding:8px;background:#f0fdf4;page-break-inside:auto}
    .summary-title{font-size:12px;font-weight:bold;text-align:center;margin:0 0 6px 0;padding:6px;border:2px solid #0a4d29;background:#0a4d29;color:#fff;page-break-after:avoid}
    .summary-table{width:100%;border-collapse:collapse}
    .summary-table td{border:1px solid #333;padding:4px}
    .summary-table tr{page-break-inside:avoid}
    .desc-col{width:55%;text-align:right}.amount-col{width:35%;text-align:left;font-family:monospace;font-weight:bold}.unit-col{width:10%;text-align:center;font-weight:bold}
    .grand-total{background:#d0d0d0;font-weight:bold;border-top:3px solid #000;border-bottom:3px solid #000}
    .footer-area{margin-top:15px;border-top:1px double #999;padding-top:8px;font-size:9px;color:#666;text-align:center}
    .chart-print-block{page-break-inside:avoid}
    .signatures-area{page-break-inside:avoid}
    @media print{
      @page{size:297mm 210mm;margin:10mm}
      html,body{background:none!important;padding:0!important;margin:0!important;height:auto!important;overflow:visible!important}
      body.orient-portrait .print-content{max-width:210mm}
      body.orient-landscape .print-content{max-width:297mm}
      .no-print{display:none!important}
      .print-content{background:none!important;padding:8mm!important;box-shadow:none!important;overflow:visible!important;min-height:0!important}
      .print-content *{overflow:visible}
      .section-block{page-break-inside:auto}
      .cost-table thead{display:table-header-group}
    }
  </style>
</head>
<body class="orient-landscape">
  <div class="print-panel no-print">
    <div class="ctl">
      <label>سایز فونت:</label>
      <input type="range" id="fsRange" min="7" max="14" step="0.5" value="11" oninput="applyPrintSettings()">
      <span id="fsVal">11</span>px
    </div>
    <div class="ctl">
      <label>فاصله سلول:</label>
      <input type="range" id="padRange" min="0" max="8" step="0.5" value="2" oninput="applyPrintSettings()">
      <span id="padVal">2</span>px
    </div>
    <div class="ctl">
      <label>جهت صفحه:</label>
      <select id="orient" onchange="applyPrintSettings()">
        <option value="landscape">A4 افقی</option>
        <option value="portrait">A4 عمودی</option>
      </select>
    </div>
    <div class="ctl">
      <label>حاشیه (mm):</label>
      <input type="number" id="marginInp" value="8" min="4" max="25" style="width:50px" onchange="applyPrintSettings()">
    </div>
    <button class="btn" onclick="window.print()">چاپ (PDF)</button>
  </div>
  <div class="print-content">
  <div class="header-area">
    <div class="header-top">
      <div class="logo-area">${clientLogo}</div>
      <div class="title-area">
        <h1 class="main-title">گزارش آنالیز بها</h1>
        <h2 class="project-title">${ca.name}</h2>
      </div>
      <div class="logo-area">${companyLogo}</div>
    </div>
    <table class="info-table">
      <tr><td class="info-label">کد پروژه:</td><td>${proj.contract_number || '—'}</td>
      <td class="info-label">تاریخ تهیه:</td><td>${new Date().toLocaleDateString('fa-IR')}</td></tr>
      <tr><td class="info-label">کارفرما:</td><td>${client.name || '—'}</td>
      <td class="info-label">محل اجرا:</td><td>${proj.location || '—'}</td></tr>
      <tr><td class="info-label">پیمانکار:</td><td>${company.company_name || '—'}</td>
      <td class="info-label">وضعیت:</td><td>${proj.formatted_status || '—'}</td></tr>
    </table>
  </div>
  ${sectionsHtml}
  ${options.includeCharts && (() => {
    const base = parseFloat(String(ca.total_base_cost ?? '0')) || 0;
    const taxV = parseFloat(String(ca.total_tax ?? '0')) || 0;
    const insV = parseFloat(String(ca.total_insurance ?? '0')) || 0;
    const prof = parseFloat(String(ca.total_profit ?? '0')) || 0;
    const chartData = [
      { name: 'هزینه پایه', value: base, color: '#0f766e' },
      { name: 'مالیات', value: taxV, color: '#0369a1' },
      { name: 'بیمه', value: insV, color: '#7c3aed' },
      { name: 'سود', value: prof, color: '#d97706' },
    ].filter((d) => d.value > 0);
    if (chartData.length === 0) return '';
    return buildChartTableHtml(chartData, (n) => fmt(n));
  })()}
  ${(() => {
    const totalCostNum = parseFloat(String(ca.total_cost ?? '0')) || 0;
    const taxV = parseFloat(String(ca.total_tax ?? '0')) || 0;
    const insV = parseFloat(String(ca.total_insurance ?? '0')) || 0;
    const taxRatio = totalCostNum > 0 ? taxV / totalCostNum : 0;
    const insuranceRatio = totalCostNum > 0 ? insV / totalCostNum : 0;
    const taxDisplayPct = (ca.tax_percentage != null && ca.tax_percentage !== '') ? parseFloat(String(ca.tax_percentage)) : taxRatio * 100;
    const insuranceDisplayPct = (ca.insurance_percentage != null && ca.insurance_percentage !== '') ? parseFloat(String(ca.insurance_percentage)) : insuranceRatio * 100;
    const totalDeductionPct = taxDisplayPct + insuranceDisplayPct;
    const formatPercentPrint = (val: number) => {
      const r = Number(val.toFixed(4));
      const approx = Math.abs(r - val) > 1e-10;
      const toPersian = (s: string) => s.replace(/\d/g, (d) => PERSIAN_DIGITS[parseInt(d, 10)]);
      return (approx ? '≈' : '') + toPersian(r.toString());
    };
    const adjPct = (ca.has_adjustment && ca.adjustment_predicted_percentage != null) ? parseFloat(String(ca.adjustment_predicted_percentage)) || 0 : 0;
    const adjAmount = Math.ceil(totalCostNum * (adjPct / 100));
    const totalWithAdj = totalCostNum + adjAmount;
    const taxOnAdj = Math.ceil(adjAmount * taxRatio);
    const insuranceOnAdj = Math.ceil(adjAmount * insuranceRatio);
    const adjAfterDeductions = adjAmount - taxOnAdj - insuranceOnAdj;
    const profitNum = parseFloat(String(ca.total_profit ?? '0')) || 0;
    const profitWithAdj = Math.ceil(profitNum + adjAfterDeductions);
    const showAdj = options.showAdjustmentInPrint && ca.has_adjustment && adjPct !== 0;
    const includeAdj = options.includeAdjustmentInPrint && showAdj;
    const finalTotal = includeAdj ? totalWithAdj : ca.total_cost;
    const finalLabel = includeAdj ? 'قیمت نهایی پروژه (با تعدیل):' : 'قیمت نهایی پروژه:';
    const prepTotalPct = ca.prepayment_total_percent != null ? parseFloat(String(ca.prepayment_total_percent)) || 0 : 0;
    const prepShares = options.includePrepaymentInPrint !== false && (ca.has_prepayment && Array.isArray(ca.prepayment_percentages) && ca.prepayment_percentages.length) ? ca.prepayment_percentages.slice(0, ca.prepayment_installments ?? 0).map((p: number) => parseFloat(String(p)) || 0) : [];
    const prepEffectivePcts = prepTotalPct > 0 ? prepShares.map((s: number) => (prepTotalPct * s) / 100) : prepShares;
    const prepRows = prepEffectivePcts.length ? prepEffectivePcts.map((pct: number, i: number) => {
      const amt = (totalCostNum * pct) / 100;
      return `<tr><td class="desc-col">قسط ${i + 1} (${fmtQty(pct)}٪):</td><td class="amount-col"><strong>${fmt(amt)}</strong></td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>`;
    }).join('') : '';
    const adjRows = showAdj ? `
      <tr><td class="desc-col">تعدیل (پیش‌بینی ${formatPercentPrint(adjPct)}٪):</td><td class="amount-col"><strong>${fmt(adjAmount)}</strong></td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>
      <tr><td class="desc-col">کسورات از تعدیل (مالیات ${formatPercentPrint(taxDisplayPct)}٪):</td><td class="amount-col">− ${fmt(taxOnAdj)}</td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>
      <tr><td class="desc-col">کسورات از تعدیل (بیمه ${formatPercentPrint(insuranceDisplayPct)}٪):</td><td class="amount-col">− ${fmt(insuranceOnAdj)}</td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>
      <tr><td class="desc-col">خالص تعدیل به سود (بعد از کسر ${formatPercentPrint(totalDeductionPct)}٪ کسورات):</td><td class="amount-col"><strong>${fmt(adjAfterDeductions)}</strong></td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>
      <tr><td class="desc-col">مبلغ با تعدیل:</td><td class="amount-col"><strong>${fmt(totalWithAdj)}</strong></td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>
      <tr class="total-row"><td class="desc-col"><strong>سود با تعدیل:</strong></td><td class="amount-col"><strong>${fmt(profitWithAdj)}</strong></td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>` : '';
    const extraCoeffs = Array.isArray(ca.extra_coefficients) ? ca.extra_coefficients : [];
    const extraCoeffTotalPrint = extraCoeffs.reduce((sum: number, c: { name: string; percentage: number }) => {
      const pct = typeof c.percentage === 'number' ? c.percentage : parseFloat(String(c.percentage)) || 0;
      return sum + Math.ceil((totalCostNum * pct) / 100);
    }, 0);
    const profitAfterExtraPrint = Math.max(0, profitNum - extraCoeffTotalPrint);
    const profitWithAdjAfterExtraPrint = Math.max(0, profitWithAdj - extraCoeffTotalPrint);
    const extraCoeffRows = extraCoeffs.length ? extraCoeffs.map((c: { name: string; percentage: number }) => {
      const pct = typeof c.percentage === 'number' ? c.percentage : parseFloat(String(c.percentage)) || 0;
      const amt = Math.ceil((totalCostNum * pct) / 100);
      return `<tr><td class="desc-col">${c.name} (${formatPercentPrint(pct)}٪):</td><td class="amount-col"><strong>${fmt(amt)}</strong></td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>`;
    }).join('') : '';
    const extraCoeffSummaryRows = extraCoeffs.length ? `
      <tr class="total-row"><td class="desc-col">مجموع ضرایب اضافی (کسورات از سود):</td><td class="amount-col">− ${fmt(extraCoeffTotalPrint)}</td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>
      <tr><td class="desc-col"><strong>سود خالص (بعد از کسر ضرایب اضافی):</strong></td><td class="amount-col"><strong>${fmt(profitAfterExtraPrint)}</strong></td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>
      ${showAdj ? `<tr><td class="desc-col"><strong>سود با تعدیل، بعد از کسر ضرایب اضافی:</strong></td><td class="amount-col"><strong>${fmt(profitWithAdjAfterExtraPrint)}</strong></td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>` : ''}` : '';
    const hasMaintenance = ca.has_maintenance_period && ca.maintenance_retention_percentage != null;
    const maintenancePct = hasMaintenance ? parseFloat(String(ca.maintenance_retention_percentage)) || 0 : 0;
    const maintenanceAmount = hasMaintenance ? Math.ceil((totalCostNum * maintenancePct) / 100) : 0;
    const maintenanceRows = hasMaintenance ? `
      <tr><td class="desc-col">دوره نگهداری (${ca.maintenance_duration || '—'}):</td><td class="amount-col">—</td><td class="unit-col">—</td></tr>
      <tr><td class="desc-col">مبلغ بلوکه شده بعد از پیمان (${formatPercentPrint(maintenancePct)}٪):</td><td class="amount-col"><strong>${fmt(maintenanceAmount)}</strong></td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>` : '';
    return `
  <div class="summary-section">
    <h3 class="summary-title">خلاصه محاسبات مالی</h3>
    <table class="summary-table">
      <tr><td class="desc-col">مجموع هزینه‌های مستقیم:</td><td class="amount-col"><strong>${fmt(ca.total_base_cost)}</strong></td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>
      <tr class="total-row"><td class="desc-col"><strong>هزینه پایه:</strong></td><td class="amount-col"><strong>${fmt(ca.total_base_cost)}</strong></td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>
      <tr><td class="desc-col">مالیات (${fmtQty(ca.tax_percentage)}٪):</td><td class="amount-col"><strong>${fmt(ca.total_tax)}</strong></td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>
      <tr><td class="desc-col">بیمه:</td><td class="amount-col"><strong>${fmt(ca.total_insurance)}</strong></td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>
      <tr><td class="desc-col">سود (${fmtQty(data.effective_profit_percentage)}٪):</td><td class="amount-col"><strong>${fmt(ca.total_profit)}</strong></td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>
      ${(data.contractor_coefficient != null && data.contractor_coefficient > 0) ? `<tr><td class="desc-col">ضریب پیمانکار:</td><td class="amount-col"><strong>${fmtCoef(data.contractor_coefficient)}</strong></td><td class="unit-col">—</td></tr>` : ''}
      ${adjRows}
      ${prepRows}
      ${extraCoeffRows}
      ${extraCoeffSummaryRows}
      ${maintenanceRows}
      <tr class="grand-total"><td class="desc-col"><strong>${finalLabel}</strong></td><td class="amount-col"><strong>${fmt(finalTotal)}</strong></td><td class="unit-col"><strong>${ca.currency ?? 'IRR'}</strong></td></tr>
    </table>
  </div>`;
  })()}
  ${(options.signatures && options.signatures.length > 0) ? `
  <div class="signatures-area" style="margin-top:20px;border-top:1px double #999;padding-top:12px;display:flex;justify-content:space-around;gap:15px;">
    ${options.signatures.map((s) => `
      <div style="text-align:center;width:28%;border:1px solid #333;padding:8px;font-size:9px;">
        <p style="margin:0 0 6px 0;font-weight:bold">${s.position || 'امضا'}</p>
        ${s.name ? `<p style="margin:0 0 4px 0;color:#555">${s.name}</p>` : ''}
        <div style="width:80%;height:1px;border-top:1px solid #000;margin:8px auto 0"></div>
      </div>
    `).join('')}
  </div>
  ` : ''}
  <div class="footer-area" style="margin-top:12px;border-top:1px solid #ccc;padding-top:8px;font-size:9px;color:#666;text-align:center">${company.company_name} - تاریخ تولید: ${new Date().toLocaleString('fa-IR')}</div>
  </div>
  <script>
  function applyPrintSettings(){
    var fs=document.getElementById('fsRange').value;
    var pad=document.getElementById('padRange').value;
    var orient=document.getElementById('orient').value;
    var margin=document.getElementById('marginInp').value||'8';
    document.documentElement.style.setProperty('--font-size',fs+'px');
    document.documentElement.style.setProperty('--cell-pad',pad+'px');
    document.getElementById('fsVal').textContent=fs;
    document.getElementById('padVal').textContent=pad;
    document.body.classList.remove('orient-landscape','orient-portrait');
    document.body.classList.add('orient-'+orient);
    var pageStyle=document.getElementById('print-page-style');
    if(!pageStyle){pageStyle=document.createElement('style');pageStyle.id='print-page-style';document.head.appendChild(pageStyle);}
    var sizeVal=orient==='portrait'?'210mm 297mm':'297mm 210mm';
    pageStyle.textContent='@page{size:'+sizeVal+';margin:'+margin+'mm}';
  }
  applyPrintSettings();
  </script>
</body>
</html>`;
};

const AnalysisDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState<AnalysisItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const [deleteSection, setDeleteSection] = useState<AnalysisSection | null>(null);
  const [deleteItem, setDeleteItem] = useState<{ section: AnalysisSection; item: AnalysisSectionItem } | null>(null);
  const [modalSection, setModalSection] = useState<AnalysisSection | null>(null);
  const [modalItem, setModalItem] = useState<{ section: AnalysisSection; item: AnalysisSectionItem } | null>(null);
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [addItemModalSection, setAddItemModalSection] = useState<AnalysisSection | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editAnalysisOpen, setEditAnalysisOpen] = useState(false);
  const [editAnalysisForm, setEditAnalysisForm] = useState({
    project_id: '' as number | '',
    name: '',
    description: '',
    initial_estimate: '',
    has_prepayment: false,
    prepayment_installments: 3,
    prepayment_total_percent: '' as string,
    prepayment_percentages: [] as string[],
    has_adjustment: false,
    adjustment_predicted_percentage: '',
    has_maintenance_period: false,
    maintenance_duration: '',
    maintenance_retention_percentage: '',
    extra_coefficients: [] as Array<{ name: string; percentage: string }>,
  });
  const [newSection, setNewSection] = useState({ name: '', description: '', insurance_percentage: '' });
  const [newItem, setNewItem] = useState({ name: '', unit: '', quantity: '', unit_cost: '', description: '' });
  const [editForm, setEditForm] = useState({ name: '', description: '', insurance_percentage: '' });
  const [itemForm, setItemForm] = useState({ name: '', unit: '', quantity: '', unit_cost: '', description: '' });
  const [settings, setSettings] = useState({
    initial_estimate: '',
    tax_percentage: '',
    insurance_mode: 'sections' as 'global' | 'sections',
    insurance_percentage: '',
    profit_type: 'percentage_gross' as string,
    profit_percentage: '',
    profit_amount: '',
    profit_amount_meaning: 'before_adjustment' as 'before_adjustment' | 'with_adjustment',
    auto_calculate_coefficient: true,
    target_contractor_coefficient: '',
  });
  const [estimateEditing, setEstimateEditing] = useState(false);
  const [estimateDraft, setEstimateDraft] = useState('');
  const [savingEstimate, setSavingEstimate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printIncludeCharts, setPrintIncludeCharts] = useState(true);
  const [printAction, setPrintAction] = useState<'print' | 'pdf'>('print');
  const [printDecimalPlacesAmounts, setPrintDecimalPlacesAmounts] = useState(0);
  const [printDecimalPlacesQuantity, setPrintDecimalPlacesQuantity] = useState(2);
  const [printDecimalPlacesCoefficient, setPrintDecimalPlacesCoefficient] = useState(4);
  const [printSignatures, setPrintSignatures] = useState<Array<{ position: string; name: string }>>([
    { position: 'پیمانکار', name: '' },
    { position: 'کارفرما', name: '' },
    { position: 'ناظر', name: '' },
  ]);
  /** در خروجی چاپ/PDF تعدیل را نشان بده (مبلغ و درصد) */
  const [printShowAdjustment, setPrintShowAdjustment] = useState(true);
  /** در خروجی چاپ/PDF مبالغ را با تعدیل محاسبه کن (برای استفاده داخلی/سود با تعدیل). اگر خاموش باشد قیمت به کارفرما بدون تعدیل است. */
  const [printIncludeAdjustmentInAmounts, setPrintIncludeAdjustmentInAmounts] = useState(false);
  /** نمایش مبالغ اقساط پیش‌پرداخت در خروجی چاپ/PDF */
  const [printIncludePrepayment, setPrintIncludePrepayment] = useState(true);

  /** کپی کل آنالیز به یک آنالیز دیگر: اول پروژه، بعد آنالیزهای آن پروژه */
  const [copyWholeToAnalysisOpen, setCopyWholeToAnalysisOpen] = useState(false);
  const [copyWholeProjectId, setCopyWholeProjectId] = useState<number | ''>('');
  const [copyWholeTargetAnalysisId, setCopyWholeTargetAnalysisId] = useState<number | ''>('');
  const [projectsForCopy, setProjectsForCopy] = useState<ProjectOption[]>([]);
  const [copyWholeAnalyses, setCopyWholeAnalyses] = useState<AnalysisItem[]>([]);

  const [copySectionModal, setCopySectionModal] = useState<AnalysisSection | null>(null);
  const [copySectionProjectId, setCopySectionProjectId] = useState<number | ''>('');
  const [copySectionTargetId, setCopySectionTargetId] = useState<number | ''>('');
  const [copySectionAnalyses, setCopySectionAnalyses] = useState<AnalysisItem[]>([]);

  const [copyItemsModal, setCopyItemsModal] = useState<AnalysisSection | null>(null);
  const [copyItemIds, setCopyItemIds] = useState<number[]>([]);
  const [copyItemsProjectId, setCopyItemsProjectId] = useState<number | ''>('');
  const [copyItemsTargetAnalysisId, setCopyItemsTargetAnalysisId] = useState<number | ''>('');
  const [copyItemsAnalyses, setCopyItemsAnalyses] = useState<AnalysisItem[]>([]);
  const [copyItemsTargetSectionId, setCopyItemsTargetSectionId] = useState<number | ''>('');
  const [targetAnalysisSections, setTargetAnalysisSections] = useState<AnalysisSection[]>([]);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [sectionMenuOpenId, setSectionMenuOpenId] = useState<number | null>(null);
  const [detailTab, setDetailTab] = useState<'sections' | 'insights' | 'ai'>('sections');
  const [exportingExcel, setExportingExcel] = useState(false);

  const load = () => {
    if (!id) return;
    setLoading(true);
    projectAnalysisApi
      .get(parseInt(id, 10))
      .then((res) => {
        setAnalysis(res.data);
        setExpandedSections(new Set(res.data.sections?.map((s) => s.id) ?? []));
        setSettings({
          initial_estimate: res.data.initial_estimate != null && res.data.initial_estimate !== ''
            ? String(res.data.initial_estimate).replace(/[,٬\s]/g, '')
            : (res.data.project?.initial_estimate != null ? String(res.data.project.initial_estimate).replace(/[,٬\s]/g, '') : ''),
          tax_percentage: res.data.tax_percentage ?? '',
          insurance_mode: res.data.insurance_mode === 'global' ? 'global' : 'sections',
          insurance_percentage: res.data.insurance_percentage ?? '',
          profit_type: res.data.profit_type ?? 'percentage_gross',
          profit_percentage: res.data.profit_percentage ?? '',
          profit_amount: res.data.profit_amount != null ? String(res.data.profit_amount) : '',
          profit_amount_meaning: (res.data.profit_amount_meaning === 'with_adjustment' ? 'with_adjustment' : 'before_adjustment'),
          auto_calculate_coefficient: res.data.auto_calculate_coefficient ?? true,
          target_contractor_coefficient: res.data.target_contractor_coefficient != null ? String(res.data.target_contractor_coefficient) : '',
        });
        setEstimateEditing(false);
      })
      .catch(() => setAnalysis(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(), [id]);

  const toggleSection = (sectionId: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const handleRecalculate = async () => {
    if (!analysis) return;
    try {
      const res = await projectAnalysisApi.recalculate(analysis.id);
      setAnalysis(res.data);
    } catch {}
  };

  const handleExportExcel = async () => {
    if (!analysis) return;
    setExportingExcel(true);
    setHeaderMenuOpen(false);
    try {
      await projectAnalysisApi.exportExcel(analysis.id, analysis.name);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'خطا در دریافت فایل Excel');
    } finally { setExportingExcel(false); }
  };

  const openEditAnalysisModal = () => {
    if (!analysis) return;
    const n = analysis.prepayment_installments ?? 3;
    const basePct = 100 / n;
    const defaultPcts = Array.from(
      { length: n },
      (_, i) => (i === n - 1 ? (100 - basePct * (n - 1)).toFixed(2) : basePct.toFixed(2))
    );
    const rawPcts = analysis.prepayment_percentages && analysis.prepayment_percentages.length >= n
      ? analysis.prepayment_percentages.slice(0, n).map((x) => parseFloat(String(x)) || 0)
      : defaultPcts.map((x) => parseFloat(x) || 0);
    const totalPercent = analysis.prepayment_total_percent != null ? parseFloat(String(analysis.prepayment_total_percent)) : null;
    const sumRaw = rawPcts.reduce((a, b) => a + b, 0);
    const pcts: string[] = sumRaw > 0 && totalPercent == null
      ? rawPcts.map((p) => (100 * p / sumRaw).toFixed(2))
      : (totalPercent != null ? rawPcts.map((p) => String(p)) : defaultPcts);
    const prepaymentTotalPercent = totalPercent != null ? String(totalPercent) : (sumRaw > 0 ? String(sumRaw.toFixed(2)) : '');
    const currentProjectId = analysis.project_id ?? analysis.project?.id;
    const estRaw = analysis.initial_estimate ?? analysis.project?.initial_estimate;
    setEditAnalysisForm({
      project_id: currentProjectId != null ? currentProjectId : '',
      name: analysis.name,
      description: analysis.description ?? '',
      initial_estimate: estRaw != null && estRaw !== '' ? String(estRaw).replace(/[,٬\s]/g, '') : '',
      has_prepayment: analysis.has_prepayment ?? false,
      prepayment_installments: n,
      prepayment_total_percent: prepaymentTotalPercent,
      prepayment_percentages: pcts.length >= n ? pcts : [...pcts, ...defaultPcts.slice(pcts.length, n)],
      has_adjustment: analysis.has_adjustment ?? false,
      adjustment_predicted_percentage: analysis.adjustment_predicted_percentage != null ? String(analysis.adjustment_predicted_percentage) : '',
      has_maintenance_period: analysis.has_maintenance_period ?? false,
      maintenance_duration: analysis.maintenance_duration ?? '',
      maintenance_retention_percentage: analysis.maintenance_retention_percentage != null ? String(analysis.maintenance_retention_percentage) : '',
      extra_coefficients: (analysis.extra_coefficients ?? []).map((c) => ({ name: c.name, percentage: String(c.percentage) })),
    });
    projectAnalysisApi.projects().then((res) => setProjectsForCopy(res.data ?? [])).catch(() => setProjectsForCopy([]));
    setEditAnalysisOpen(true);
  };

  const handleSaveEditAnalysis = async () => {
    if (!analysis || !editAnalysisForm.name.trim()) return;
    setSaving(true);
    try {
      const n = editAnalysisForm.has_prepayment ? Math.max(1, Math.min(24, editAnalysisForm.prepayment_installments)) : 0;
      const prepaymentPercentages =
        n > 0 && editAnalysisForm.prepayment_percentages.length >= n
          ? editAnalysisForm.prepayment_percentages.slice(0, n).map((s) => parseFloat(s) || 0)
          : null;
      const prepaymentTotalPercentNum = editAnalysisForm.has_prepayment && editAnalysisForm.prepayment_total_percent
        ? parseFloat(editAnalysisForm.prepayment_total_percent) : null;
      const payload: Parameters<typeof projectAnalysisApi.update>[1] = {
        name: editAnalysisForm.name.trim(),
        description: editAnalysisForm.description.trim() || undefined,
        initial_estimate: editAnalysisForm.initial_estimate
          ? parseFloat(parseNumberInput(editAnalysisForm.initial_estimate)) || null
          : null,
        has_prepayment: editAnalysisForm.has_prepayment,
        prepayment_installments: editAnalysisForm.has_prepayment ? n : null,
        prepayment_total_percent: editAnalysisForm.has_prepayment ? prepaymentTotalPercentNum : null,
        prepayment_percentages: editAnalysisForm.has_prepayment ? prepaymentPercentages : null,
        has_adjustment: editAnalysisForm.has_adjustment,
        adjustment_predicted_percentage:
          editAnalysisForm.has_adjustment && editAnalysisForm.adjustment_predicted_percentage
            ? parseFloat(editAnalysisForm.adjustment_predicted_percentage)
            : null,
      };
      if (editAnalysisForm.project_id !== '' && typeof editAnalysisForm.project_id === 'number') {
        payload.project_id = editAnalysisForm.project_id;
      }
      payload.has_maintenance_period = editAnalysisForm.has_maintenance_period;
      payload.maintenance_duration = editAnalysisForm.has_maintenance_period && editAnalysisForm.maintenance_duration.trim() ? editAnalysisForm.maintenance_duration.trim() : undefined;
      payload.maintenance_retention_percentage = editAnalysisForm.has_maintenance_period && editAnalysisForm.maintenance_retention_percentage ? parseFloat(editAnalysisForm.maintenance_retention_percentage) : undefined;
      const extraCoefs = editAnalysisForm.extra_coefficients.filter((c) => c.name.trim() && c.percentage !== '');
      payload.extra_coefficients = extraCoefs.length > 0 ? extraCoefs.map((c) => ({ name: c.name.trim(), percentage: parseFloat(c.percentage) || 0 })) : undefined;
      const res = await projectAnalysisApi.update(analysis.id, payload);
      setAnalysis(res.data);
      setEditAnalysisOpen(false);
    } catch {
      setSaving(false);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!analysis) return;
    setSaving(true);
    try {
      const estimateNum = settings.initial_estimate
        ? parseFloat(parseNumberInput(settings.initial_estimate))
        : null;
      const res = await projectAnalysisApi.updateSettings(analysis.id, {
        initial_estimate: estimateNum != null && !Number.isNaN(estimateNum) ? estimateNum : null,
        tax_percentage: settings.tax_percentage ? parseFloat(settings.tax_percentage) : null,
        insurance_mode: settings.insurance_mode,
        insurance_percentage: settings.insurance_percentage ? parseFloat(settings.insurance_percentage) : 0,
        profit_type: settings.profit_type,
        profit_percentage: settings.profit_percentage ? parseFloat(settings.profit_percentage) : null,
        profit_amount: settings.profit_amount ? parseFloat(settings.profit_amount) : null,
        profit_amount_meaning: settings.profit_type === 'amount' ? settings.profit_amount_meaning : undefined,
        auto_calculate_coefficient: settings.auto_calculate_coefficient,
        target_contractor_coefficient: settings.target_contractor_coefficient ? parseFloat(settings.target_contractor_coefficient) : null,
      });
      setAnalysis(res.data);
      setSettingsOpen(false);
    } catch {}
    finally { setSaving(false); }
  };

  const startEstimateEdit = () => {
    if (!analysis) return;
    const raw = analysis.initial_estimate ?? analysis.project?.initial_estimate;
    setEstimateDraft(raw != null && raw !== '' ? String(raw).replace(/[,٬\s]/g, '') : '');
    setEstimateEditing(true);
  };

  const handleSaveEstimateInline = async () => {
    if (!analysis) return;
    setSavingEstimate(true);
    try {
      const num = estimateDraft ? parseFloat(parseNumberInput(estimateDraft)) : null;
      const res = await projectAnalysisApi.updateSettings(analysis.id, {
        initial_estimate: num != null && !Number.isNaN(num) ? num : null,
      });
      setAnalysis(res.data);
      setSettings((p) => ({
        ...p,
        initial_estimate: num != null && !Number.isNaN(num) ? String(num) : '',
      }));
      setEstimateEditing(false);
    } catch {
      /* keep editing */
    } finally {
      setSavingEstimate(false);
    }
  };

  const handleAddSection = async () => {
    if (!analysis || !newSection.name.trim()) return;
    setSaving(true);
    try {
      await projectAnalysisApi.storeSection(analysis.id, {
        name: newSection.name.trim(),
        description: newSection.description.trim() || undefined,
        insurance_percentage: newSection.insurance_percentage ? parseFloat(newSection.insurance_percentage) : undefined,
      });
      setNewSection({ name: '', description: '', insurance_percentage: '' });
      setAddSectionOpen(false);
      load();
    } catch {}
    finally { setSaving(false); }
  };

  const handleSaveSection = async () => {
    if (!analysis || !modalSection || !editForm.name.trim()) return;
    setSaving(true);
    try {
      await projectAnalysisApi.updateSection(analysis.id, modalSection.id, {
        name: editForm.name.trim(),
        description: editForm.description.trim() || undefined,
        insurance_percentage: editForm.insurance_percentage ? parseFloat(editForm.insurance_percentage) : undefined,
      });
      setModalSection(null);
      load();
    } catch {}
    finally { setSaving(false); }
  };

  const handleDeleteSection = async () => {
    if (!analysis || !deleteSection) return;
    try {
      await projectAnalysisApi.destroySection(analysis.id, deleteSection.id);
      setDeleteSection(null);
      load();
    } catch {}
  };

  const handleAddItem = async () => {
    if (!analysis || !addItemModalSection || !newItem.name.trim() || !newItem.quantity || !newItem.unit_cost) return;
    const qty = parseFloat(newItem.quantity);
    const cost = parseFloat(newItem.unit_cost);
    if (isNaN(qty) || isNaN(cost)) return;
    setSaving(true);
    try {
      await projectAnalysisApi.storeItem(analysis.id, addItemModalSection.id, {
        name: newItem.name.trim(),
        unit: newItem.unit || undefined,
        quantity: qty,
        unit_cost: cost,
        description: newItem.description.trim() || undefined,
      });
      setAddItemModalSection(null);
      setNewItem({ name: '', unit: '', quantity: '', unit_cost: '', description: '' });
      load();
    } catch {}
    finally { setSaving(false); }
  };

  const handleSaveItem = async () => {
    if (!analysis || !modalItem || !itemForm.name.trim() || !itemForm.quantity || !itemForm.unit_cost) return;
    const qty = parseFloat(itemForm.quantity);
    const cost = parseFloat(itemForm.unit_cost);
    if (isNaN(qty) || isNaN(cost)) return;
    setSaving(true);
    try {
      await projectAnalysisApi.updateItem(analysis.id, modalItem.section.id, modalItem.item.id, {
        name: itemForm.name.trim(),
        unit: itemForm.unit || undefined,
        quantity: qty,
        unit_cost: cost,
        description: itemForm.description.trim() || undefined,
      });
      setModalItem(null);
      load();
    } catch {}
    finally { setSaving(false); }
  };

  const handleDeleteItem = async () => {
    if (!analysis || !deleteItem) return;
    try {
      await projectAnalysisApi.destroyItem(analysis.id, deleteItem.section.id, deleteItem.item.id);
      setDeleteItem(null);
      load();
    } catch {}
  };

  const openPrintModal = (action: 'print' | 'pdf') => {
    setPrintAction(action);
    setPrintModalOpen(true);
  };

  const openCopyWholeToAnalysisModal = () => {
    setCopyWholeProjectId('');
    setCopyWholeTargetAnalysisId('');
    setCopyWholeAnalyses([]);
    setCopyWholeToAnalysisOpen(true);
    projectAnalysisApi.projects().then((res) => setProjectsForCopy(res.data ?? [])).catch(() => setProjectsForCopy([]));
  };

  useEffect(() => {
    if (!copyWholeToAnalysisOpen || !copyWholeProjectId) {
      setCopyWholeAnalyses([]);
      setCopyWholeTargetAnalysisId('');
      return;
    }
    setCopyWholeTargetAnalysisId('');
    const pid = Number(copyWholeProjectId);
    projectAnalysisApi
      .list({ project_id: pid, per_page: 100 })
      .then((res) =>
        setCopyWholeAnalyses(
          res.data.filter((a) => a.id !== analysis?.id && (a.project_id === pid || a.project?.id === pid))
        )
      )
      .catch(() => setCopyWholeAnalyses([]));
  }, [copyWholeToAnalysisOpen, copyWholeProjectId, analysis?.id]);

  const openCopySectionModal = (section: AnalysisSection) => {
    setCopySectionModal(section);
    setCopySectionProjectId('');
    setCopySectionTargetId('');
    setCopySectionAnalyses([]);
    projectAnalysisApi.projects().then((res) => setProjectsForCopy(res.data ?? [])).catch(() => setProjectsForCopy([]));
  };

  useEffect(() => {
    if (!copySectionModal || !copySectionProjectId) {
      setCopySectionAnalyses([]);
      setCopySectionTargetId('');
      return;
    }
    setCopySectionTargetId('');
    const pid = Number(copySectionProjectId);
    projectAnalysisApi
      .list({ project_id: pid, per_page: 100 })
      .then((res) =>
        setCopySectionAnalyses(
          res.data.filter((a) => a.id !== analysis?.id && (a.project_id === pid || a.project?.id === pid))
        )
      )
      .catch(() => setCopySectionAnalyses([]));
  }, [copySectionModal, copySectionProjectId, analysis?.id]);

  const openCopyItemsModal = (section: AnalysisSection) => {
    setCopyItemsModal(section);
    setCopyItemIds([]);
    setCopyItemsProjectId('');
    setCopyItemsTargetAnalysisId('');
    setCopyItemsAnalyses([]);
    setCopyItemsTargetSectionId('');
    setTargetAnalysisSections([]);
    projectAnalysisApi.projects().then((res) => setProjectsForCopy(res.data ?? [])).catch(() => setProjectsForCopy([]));
  };

  useEffect(() => {
    if (!copyItemsModal || !copyItemsProjectId) {
      setCopyItemsAnalyses([]);
      setCopyItemsTargetAnalysisId('');
      setTargetAnalysisSections([]);
      setCopyItemsTargetSectionId('');
      return;
    }
    setCopyItemsTargetAnalysisId('');
    setTargetAnalysisSections([]);
    setCopyItemsTargetSectionId('');
    const pid = Number(copyItemsProjectId);
    projectAnalysisApi
      .list({ project_id: pid, per_page: 100 })
      .then((res) =>
        setCopyItemsAnalyses(
          res.data.filter((a) => a.id !== analysis?.id && (a.project_id === pid || a.project?.id === pid))
        )
      )
      .catch(() => setCopyItemsAnalyses([]));
  }, [copyItemsModal, copyItemsProjectId, analysis?.id]);

  const onCopyItemsTargetAnalysisChange = (analysisId: number | '') => {
    setCopyItemsTargetAnalysisId(analysisId);
    setCopyItemsTargetSectionId('');
    if (analysisId === '' || analysisId === 0) {
      setTargetAnalysisSections([]);
      return;
    }
    projectAnalysisApi.get(analysisId).then((res) => setTargetAnalysisSections(res.data.sections ?? [])).catch(() => setTargetAnalysisSections([]));
  };

  const handleCopyWholeToAnalysis = async () => {
    if (!analysis || !copyWholeTargetAnalysisId || !analysis.sections?.length) return;
    setSaving(true);
    try {
      const res = await projectAnalysisApi.copySections(analysis.id, {
        target_analysis_id: copyWholeTargetAnalysisId,
        section_ids: analysis.sections.map((s) => s.id),
      });
      setCopyWholeToAnalysisOpen(false);
      setCopyWholeTargetAnalysisId('');
      if (res.data?.id && res.data.id !== analysis.id) {
        navigate(`/analyses/${res.data.id}`);
      } else {
        load();
      }
    } catch {
      setSaving(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCopySection = async () => {
    if (!analysis || !copySectionModal || !copySectionTargetId) return;
    setSaving(true);
    try {
      const res = await projectAnalysisApi.copySections(analysis.id, {
        target_analysis_id: copySectionTargetId,
        section_ids: [copySectionModal.id],
      });
      setCopySectionModal(null);
      setCopySectionTargetId('');
      if (res.data?.id && res.data.id !== analysis.id) {
        navigate(`/analyses/${res.data.id}`);
      } else {
        load();
      }
    } catch {
      setSaving(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCopyItems = async () => {
    if (!analysis || !copyItemsModal || !copyItemsTargetSectionId || copyItemIds.length === 0) return;
    setSaving(true);
    try {
      const res = await projectAnalysisApi.copyItems(analysis.id, copyItemsModal.id, {
        target_section_id: copyItemsTargetSectionId,
        item_ids: copyItemIds,
      });
      setCopyItemsModal(null);
      setCopyItemIds([]);
      setCopyItemsTargetAnalysisId('');
      setCopyItemsTargetSectionId('');
      if (res.data?.id && res.data.id !== analysis.id) {
        navigate(`/analyses/${res.data.id}`);
      } else {
        load();
      }
    } catch {
      setSaving(false);
    } finally {
      setSaving(false);
    }
  };

  const toggleCopyItem = (itemId: number) => {
    setCopyItemIds((prev) => (prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]));
  };

  const projectOptions: SearchableSelectOption[] = useMemo(
    () =>
      projectsForCopy.map((p) => ({
        value: p.id,
        label: p.original_name || p.name,
        subLabel: [p.contract_number, p.status].filter(Boolean).join(' · '),
      })),
    [projectsForCopy]
  );

  const copyWholeAnalysisOptions: SearchableSelectOption[] = useMemo(
    () =>
      copyWholeAnalyses.map((a) => ({
        value: a.id,
        label: a.name,
        subLabel: a.sections_count != null ? `${a.sections_count} بخش` : undefined,
      })),
    [copyWholeAnalyses]
  );

  const copySectionAnalysisOptions: SearchableSelectOption[] = useMemo(
    () =>
      copySectionAnalyses.map((a) => ({
        value: a.id,
        label: a.name,
        subLabel: a.sections_count != null ? `${a.sections_count} بخش` : undefined,
      })),
    [copySectionAnalyses]
  );

  const copyItemsAnalysisOptions: SearchableSelectOption[] = useMemo(
    () =>
      copyItemsAnalyses.map((a) => ({
        value: a.id,
        label: a.name,
        subLabel: a.sections_count != null ? `${a.sections_count} بخش` : undefined,
      })),
    [copyItemsAnalyses]
  );

  const copyItemsSectionOptions: SearchableSelectOption[] = useMemo(
    () =>
      targetAnalysisSections.map((s) => ({
        value: s.id,
        label: s.name,
        subLabel: s.items?.length ? `${s.items.length} آیتم` : undefined,
      })),
    [targetAnalysisSections]
  );

  const doPrintOrPdf = async () => {
    if (!analysis || !id) return;
    setPrintModalOpen(false);
    try {
      const res = await projectAnalysisApi.printData(analysis.id);
      const content = buildPrintHtmlWysiwyg(res.data, {
        includeCharts: printIncludeCharts,
        signatures: printSignatures.filter((s) => (s.position || '').trim() || (s.name || '').trim()),
        decimalPlacesAmounts: printDecimalPlacesAmounts,
        decimalPlacesQuantity: printDecimalPlacesQuantity,
        decimalPlacesCoefficient: printDecimalPlacesCoefficient,
        showAdjustmentInPrint: printShowAdjustment,
        includeAdjustmentInPrint: printIncludeAdjustmentInAmounts,
        includePrepaymentInPrint: printIncludePrepayment,
      });
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;
      printWindow.document.write(content);
      printWindow.document.close();
    } catch {}
  };

  if (loading && !analysis) {
    return <LoadingSpinner variant="page" />;
  }

  if (!analysis) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">آنالیز یافت نشد.</p>
        <Link to="/analyses" className="text-teal-700 mt-2 inline-block font-bold hover:underline">
          بازگشت به لیست
        </Link>
      </div>
    );
  }

  const base = parseAmount(analysis.total_base_cost);
  const tax = parseAmount(analysis.total_tax);
  const ins = parseAmount(analysis.total_insurance);
  const profit = parseAmount(analysis.total_profit);
  const totalCostNum = parseAmount(analysis.total_cost);
  const totalsVerified = verifyTotalsSum(base, tax, ins, profit, totalCostNum);
  const insuranceDisplayPct = effectiveInsurancePctOnTotal(totalCostNum, ins);
  const taxDisplayPct = analysis.tax_percentage != null && analysis.tax_percentage !== ''
    ? parseAmount(analysis.tax_percentage)
    : (totalCostNum > 0 ? (tax / totalCostNum) * 100 : 0);
  const chartData = [
    { name: 'هزینه پایه', value: base, color: CHART_COLORS[0] },
    { name: 'مالیات', value: tax, color: CHART_COLORS[1] },
    { name: 'بیمه', value: ins, color: CHART_COLORS[2] },
    { name: 'سود', value: profit, color: CHART_COLORS[3] },
  ].filter((d) => d.value > 0);

  const clientName = analysis.project?.client?.name ?? '—';
  const initialEst = parseAmount(analysis.initial_estimate ?? analysis.project?.initial_estimate);
  const contractorCoef = (initialEst > 0 && totalCostNum > 0) ? totalCostNum / initialEst : null;

  // تعدیل: مبلغ فعلی + درصد تعدیل → مبلغ تعدیل و مبلغ با تعدیل؛ از مبلغ تعدیل هم مالیات و بیمه (همان نسبت) کسر می‌شود، باقی به سود اضافه → سود با تعدیل
  const adjustmentPct = analysis.has_adjustment && analysis.adjustment_predicted_percentage != null
    ? parseFloat(String(analysis.adjustment_predicted_percentage)) || 0
    : 0;
  const adjustmentAmount = Math.ceil(totalCostNum * (adjustmentPct / 100));
  const totalWithAdjustment = totalCostNum + adjustmentAmount;
  const taxRatio = totalCostNum > 0 ? tax / totalCostNum : 0;
  const insuranceRatio = totalCostNum > 0 ? ins / totalCostNum : 0;
  const taxOnAdjusted = Math.ceil(totalWithAdjustment * taxRatio);
  const insuranceOnAdjusted = Math.ceil(totalWithAdjustment * insuranceRatio);
  const taxOnAdjustment = Math.ceil(adjustmentAmount * (taxDisplayPct / 100));
  const insuranceOnAdjustment = Math.ceil(adjustmentAmount * (insuranceDisplayPct / 100));
  const adjustmentAfterDeductions = adjustmentAmount - taxOnAdjustment - insuranceOnAdjustment;
  const profitWithAdjustment = Math.ceil(profit + adjustmentAfterDeductions);
  const totalDeductionPct = taxDisplayPct + insuranceDisplayPct;

  // پیش‌پرداخت: اگر کل پیش‌پرداخت (٪) تنظیم شده، هر قسط سهمی از آن درصد است (جمع سهم‌ها ۱۰۰٪)
  const prepaymentTotalPct = analysis.prepayment_total_percent != null ? parseFloat(String(analysis.prepayment_total_percent)) || 0 : 0;
  const prepaymentShares = (analysis.has_prepayment && analysis.prepayment_percentages?.length)
    ? analysis.prepayment_percentages.slice(0, analysis.prepayment_installments ?? 0).map((p) => parseFloat(String(p)) || 0)
    : [];
  const prepaymentAmounts = prepaymentTotalPct > 0
    ? prepaymentShares.map((share) => (totalCostNum * prepaymentTotalPct / 100) * (share / 100))
    : prepaymentShares.map((pct) => (totalCostNum * pct) / 100);
  const effectivePrepaymentPcts = prepaymentTotalPct > 0
    ? prepaymentShares.map((s) => (prepaymentTotalPct * s) / 100)
    : prepaymentShares;

  // ضرایب اضافی (مثل بیمه مسئولیت): از سود کسر می‌شوند — سود خالص = سود − مجموع این مبالغ
  const extraCoeffList = analysis.extra_coefficients ?? [];
  const extraCoeffTotal = extraCoeffList.reduce((sum, c) => {
    const pct = typeof c.percentage === 'number' ? c.percentage : parseFloat(String(c.percentage)) || 0;
    return sum + Math.ceil((totalCostNum * pct) / 100);
  }, 0);
  const profitAfterExtraCoeffs = Math.max(0, profit - extraCoeffTotal);
  const profitWithAdjustmentAfterExtraCoeffs = Math.max(0, profitWithAdjustment - extraCoeffTotal);

  return (
    <div className={`w-full flex flex-col bg-slate-50 overflow-x-hidden ${detailTab === 'sections' ? 'min-h-0 flex-1 overflow-hidden' : detailTab === 'ai' || detailTab === 'insights' ? 'min-h-0 flex-1 overflow-hidden' : 'min-h-screen'}`} dir="rtl">
      {/* هدر فشرده */}
      <div className="bg-white border-b border-slate-200 shadow-sm shrink-0">
        <div className="px-3 sm:px-4 py-3 flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center shadow shrink-0">
              <Calculator size={20} className="text-white sm:w-[22px] sm:h-[22px]" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-lg font-bold text-slate-900 truncate">{analysis.name}</h1>
              <p className="text-xs text-slate-500 truncate">
                {analysis.project?.name}
                {analysis.project?.contract_number && ` (${analysis.project.contract_number})`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setDetailTab('insights')}
              className="hidden lg:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              <BarChart3 size={16} />
              آنالیز
            </button>
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={exportingExcel}
              className="hidden lg:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold bg-white border border-emerald-200 text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
              title="خروجی Excel"
            >
              <FileSpreadsheet size={16} />
              Excel
            </button>
            <button
              type="button"
              onClick={() => openPrintModal('print')}
              className="hidden lg:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              <Printer size={16} />
              چاپ
            </button>
            <button
              type="button"
              onClick={() => {
                if (analysis) {
                  const raw = analysis.initial_estimate ?? analysis.project?.initial_estimate;
                  setSettings((p) => ({
                    ...p,
                    initial_estimate: raw != null && raw !== '' ? String(raw).replace(/[,٬\s]/g, '') : '',
                  }));
                }
                setSettingsOpen(true);
              }}
              className="hidden lg:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              <Settings size={16} />
              تنظیمات
            </button>
            <Link to="/analyses" className="inline-flex items-center justify-center gap-1.5 px-2 sm:px-3 py-2 min-h-[44px] min-w-[44px] lg:min-h-0 text-slate-600 hover:bg-slate-100 active:bg-slate-200 rounded-lg text-sm font-medium transition-colors touch-manipulation">
              <ArrowRight size={18} />
              <span className="hidden sm:inline">بازگشت</span>
            </Link>
            <div className="relative">
              <button
                type="button"
                onClick={() => setHeaderMenuOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 justify-center bg-slate-100 hover:bg-slate-200 active:bg-slate-300 rounded-lg text-sm font-medium text-slate-700 transition-colors touch-manipulation"
              >
                <MoreVertical size={18} />
                <span className="hidden sm:inline">عملیات</span>
              </button>
              {headerMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setHeaderMenuOpen(false)} aria-hidden />
                  <div className="absolute right-0 sm:left-0 sm:right-auto top-full mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-xl py-1 z-50">
                    <button type="button" onClick={() => { setDetailTab('insights'); setHeaderMenuOpen(false); }} className="w-full text-right px-4 py-3 min-h-[44px] text-sm hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2 touch-manipulation">
                      <BarChart3 size={16} /> آنالیز حرفه‌ای
                    </button>
                    <button type="button" onClick={() => { setDetailTab('ai'); setHeaderMenuOpen(false); }} className="w-full text-right px-4 py-3 min-h-[44px] text-sm hover:bg-emerald-50 active:bg-emerald-100 flex items-center gap-2 touch-manipulation text-emerald-800 font-bold">
                      <Sparkles size={16} /> دستیار هوشمند / ویس
                    </button>
                    <button type="button" onClick={() => { setDetailTab('sections'); setHeaderMenuOpen(false); }} className="w-full text-right px-4 py-3 min-h-[44px] text-sm hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2 touch-manipulation">
                      <Layers size={16} /> بخش‌ها و آیتم‌ها
                    </button>
                    <div className="border-t border-slate-100 my-1" />
                    <button type="button" onClick={() => { openPrintModal('print'); setHeaderMenuOpen(false); }} className="w-full text-right px-4 py-3 min-h-[44px] text-sm hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2 touch-manipulation">
                      <Printer size={16} /> چاپ
                    </button>
                    <button type="button" onClick={() => { openPrintModal('pdf'); setHeaderMenuOpen(false); }} className="w-full text-right px-4 py-3 min-h-[44px] text-sm hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2 touch-manipulation">
                      <FileDown size={16} /> خروجی PDF
                    </button>
                    <button type="button" onClick={handleExportExcel} disabled={exportingExcel} className="w-full text-right px-4 py-3 min-h-[44px] text-sm hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2 touch-manipulation">
                      <FileSpreadsheet size={16} /> خروجی Excel
                    </button>
                    <div className="border-t border-slate-100 my-1" />
                    <button type="button" onClick={() => { openEditAnalysisModal(); setHeaderMenuOpen(false); }} className="w-full text-right px-4 py-3 min-h-[44px] text-sm hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2 touch-manipulation">
                      <Pencil size={16} /> ویرایش آنالیز
                    </button>
                    <button type="button" onClick={() => {
                      if (analysis) {
                        const raw = analysis.initial_estimate ?? analysis.project?.initial_estimate;
                        setSettings((p) => ({
                          ...p,
                          initial_estimate: raw != null && raw !== '' ? String(raw).replace(/[,٬\s]/g, '') : '',
                        }));
                      }
                      setSettingsOpen(true);
                      setHeaderMenuOpen(false);
                    }} className="w-full text-right px-4 py-3 min-h-[44px] text-sm hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2 touch-manipulation">
                      <Settings size={16} /> تنظیمات
                    </button>
                    <button type="button" onClick={() => { handleRecalculate(); setHeaderMenuOpen(false); }} className="w-full text-right px-4 py-3 min-h-[44px] text-sm hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2 touch-manipulation">
                      <RefreshCw size={16} /> محاسبه مجدد
                    </button>
                    <div className="border-t border-slate-100 my-1" />
                    <button type="button" onClick={() => { openCopyWholeToAnalysisModal(); setHeaderMenuOpen(false); }} className="w-full text-right px-4 py-3 min-h-[44px] text-sm hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2 touch-manipulation">
                      <Copy size={16} /> کپی کل آنالیز به آنالیز دیگر
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* تب‌های اصلی */}
        <div className="px-2 sm:px-4 py-2 border-t border-emerald-100 bg-white/90 grid grid-cols-3 gap-1 sm:flex sm:gap-2">
          <button
            type="button"
            onClick={() => setDetailTab('sections')}
            className={`min-w-0 inline-flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all ${detailTab === 'sections' ? 'bg-[#0a4d29] text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
          >
            <Layers size={15} className="shrink-0" />
            <span className="truncate sm:hidden">متره</span>
            <span className="hidden sm:inline">متره و بخش‌ها</span>
          </button>
          <button
            type="button"
            onClick={() => setDetailTab('insights')}
            className={`min-w-0 inline-flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all ${detailTab === 'insights' ? 'bg-[#0a4d29] text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
          >
            <BarChart3 size={15} className="shrink-0" />
            <span className="truncate sm:hidden">آنالیز</span>
            <span className="hidden sm:inline">آنالیز حرفه‌ای</span>
          </button>
          <button
            type="button"
            onClick={() => setDetailTab('ai')}
            className={`min-w-0 inline-flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all ${detailTab === 'ai' ? 'bg-[#0a4d29] text-white shadow-sm' : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200'}`}
          >
            <Sparkles size={15} className="shrink-0" />
            <span className="truncate sm:hidden">دستیار</span>
            <span className="hidden sm:inline">دستیار هوشمند</span>
          </button>
        </div>

        {detailTab === 'sections' && (
        <>
        {/* خلاصه مالی — فقط در تب بخش‌ها */}
        <div className="border-t border-slate-200/80 bg-gradient-to-l from-emerald-50/80 via-white to-slate-50">
          <div className="px-3 sm:px-6 py-4 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-bold text-emerald-800/70 tracking-wide mb-1">خلاصه مالی آنالیز</p>
                <p className="text-sm text-slate-600 truncate">
                  <span className="font-medium text-slate-800">{clientName}</span>
                </p>
              </div>
            </div>

            {/* سه شاخص اصلی: برآورد · جمع کل · ضریب */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <p className="text-xs font-bold text-slate-500">برآورد اولیه</p>
                  {!estimateEditing && (
                    <button
                      type="button"
                      onClick={startEstimateEdit}
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-teal-700 hover:bg-teal-50 px-2 py-1 rounded-md"
                      title="اصلاح برآورد اولیه"
                    >
                      <Pencil size={12} />
                      اصلاح
                    </button>
                  )}
                </div>
                {estimateEditing ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formatNumberInput(estimateDraft)}
                      onChange={(e) => setEstimateDraft(parseNumberInput(e.target.value))}
                      className="w-full px-3 py-2 border border-teal-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                      dir="ltr"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEstimateInline();
                        if (e.key === 'Escape') setEstimateEditing(false);
                      }}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSaveEstimateInline}
                        disabled={savingEstimate}
                        className="flex-1 px-3 py-1.5 bg-teal-600 text-white text-xs font-bold rounded-lg disabled:opacity-50"
                      >
                        ذخیره
                      </button>
                      <button
                        type="button"
                        onClick={() => setEstimateEditing(false)}
                        className="px-3 py-1.5 border border-slate-300 text-xs font-bold rounded-lg hover:bg-slate-50"
                      >
                        انصراف
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="font-mono font-bold text-lg text-slate-900">
                      {initialEst > 0 ? (
                        <>
                          <span className="sm:hidden">{formatCompactMoney(initialEst)}</span>
                          <span className="hidden sm:inline">{formatNumber(initialEst)}</span>
                        </>
                      ) : '—'}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">ریال · مبنای ضریب پیمانکار</p>
                  </>
                )}
              </div>

              <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-3.5 shadow-sm">
                <p className="text-xs font-bold text-emerald-800/70 mb-1.5">قیمت به کارفرما</p>
                <p className="font-mono font-black text-xl sm:text-2xl text-emerald-900">
                  <span className="sm:hidden">{formatCompactMoney(analysis.total_cost)}</span>
                  <span className="hidden sm:inline">{formatNumber(analysis.total_cost)}</span>
                </p>
                <p className="text-[11px] text-emerald-700/70 mt-0.5">{analysis.currency ?? 'ریال'}</p>
                {initialEst > 0 && totalCostNum > 0 && (
                  <p className={`text-[11px] mt-1.5 font-medium ${totalCostNum >= initialEst ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {totalCostNum >= initialEst ? '+' : ''}{formatNumber(totalCostNum - initialEst)} نسبت به برآورد
                  </p>
                )}
              </div>

              <div className="rounded-xl border border-teal-200 bg-teal-50/70 p-3.5 shadow-sm">
                <p className="text-xs font-bold text-teal-800/70 mb-1.5">ضریب پیمانکار</p>
                {contractorCoef != null ? (
                  <>
                    <p className="font-mono font-black text-xl sm:text-2xl text-teal-900" dir="ltr">{Number(contractorCoef.toFixed(4))}</p>
                    <p className="text-[11px] text-teal-700/70 mt-0.5">جمع کل ÷ برآورد اولیه</p>
                  </>
                ) : (
                  <>
                    <p className="font-mono font-bold text-lg text-slate-400">—</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">برآورد اولیه را وارد کنید</p>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 xl:hidden">
              <div className="rounded-xl bg-slate-800 text-white p-3 shadow-sm min-w-0">
                <p className="text-[10px] sm:text-xs text-slate-300 mb-1">هزینه پایه</p>
                <p className="font-bold text-sm sm:text-base break-words">{formatCompactMoney(base)}</p>
              </div>
              <div className="rounded-xl bg-violet-600 text-white p-3 shadow-sm min-w-0">
                <p className="text-[10px] sm:text-xs text-violet-200 mb-1">بیمه ({formatPercent(insuranceDisplayPct)}٪)</p>
                <p className="font-bold text-sm sm:text-base break-words">{formatCompactMoney(ins)}</p>
              </div>
              <div className="rounded-xl bg-amber-500 text-white p-3 shadow-sm min-w-0">
                <p className="text-[10px] sm:text-xs text-amber-100 mb-1">سود</p>
                <p className="font-bold text-sm sm:text-base break-words">{formatCompactMoney(profit)}</p>
              </div>
              <div className="rounded-xl bg-blue-600 text-white p-3 shadow-sm min-w-0">
                <p className="text-[10px] sm:text-xs text-blue-200 mb-1">مالیات ({formatPercent(taxDisplayPct)}٪)</p>
                <p className="font-bold text-sm sm:text-base break-words">{formatCompactMoney(tax)}</p>
              </div>
              {chartData.length > 0 && (
                <div className="rounded-xl bg-white border border-slate-200 p-2 shadow-sm col-span-2 sm:col-span-1 flex items-center justify-center min-h-[5.5rem]">
                  <div className="w-full h-20">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={chartData} cx="50%" cy="50%" innerRadius={22} outerRadius={36} paddingAngle={2} dataKey="value" nameKey="name">
                          {chartData.map((e, i) => (
                            <Cell key={i} fill={e.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v: number) => formatNumber(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            <div className={`flex flex-wrap items-center gap-2 text-xs px-3 py-2 rounded-lg border xl:hidden ${totalsVerified ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
              <span className="font-bold">بررسی جمع:</span>
              <span className="break-words">
                <span className="sm:hidden">{formatCompactMoney(base)} + {formatCompactMoney(ins)} + {formatCompactMoney(profit)} + {formatCompactMoney(tax)}</span>
                <span className="hidden sm:inline font-mono">{formatNumber(base)} + {formatNumber(ins)} + {formatNumber(profit)} + {formatNumber(tax)} = {formatNumber(base + ins + profit + tax)}</span>
              </span>
              {!totalsVerified && <span className="text-amber-700">(اختلاف با مجموع کل — «محاسبه مجدد» را بزنید)</span>}
            </div>
            <p className="text-[11px] text-slate-500 xl:hidden">بیمه درصدی از جمع نهایی است (بعد از سود). اگر همه بخش‌ها ۱۰٪ باشند و جمع کل ۱ میلیارد شود، بیمه ۱۰۰ میلیون است.</p>
          </div>
        </div>

        {/* تعدیل و پیش‌پرداخت — جلوی چشم برای مدیریت نقدینگی */}
        {(analysis.has_adjustment && adjustmentPct > 0) || (analysis.has_prepayment && prepaymentAmounts.length > 0) || analysis.has_maintenance_period || (analysis.extra_coefficients?.length ?? 0) > 0 ? (
          <details className="group border-t border-slate-200" open>
            <summary className="list-none cursor-pointer select-none px-3 sm:px-6 py-3 min-h-[44px] flex items-center gap-2 text-sm font-bold text-slate-700 hover:bg-slate-50 active:bg-slate-100 [&::-webkit-details-marker]:hidden touch-manipulation">
              <ChevronDown className="group-open:rotate-180 transition-transform shrink-0" size={18} />
              تعدیل، پیش‌پرداخت، دوره نگهداری و ضرایب اضافی
            </summary>
            <div className="px-3 sm:px-6 pb-4 space-y-4">
              {analysis.has_adjustment && adjustmentPct > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-4">
                  <h3 className="text-sm font-bold text-amber-900">تعدیل (پیش‌بینی {formatPercent(adjustmentPct)}٪)</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-slate-600 block text-xs">مبلغ فعلی (قیمت به کارفرما)</span>
                      <span className="font-mono font-bold text-slate-800">{formatNumber(analysis.total_cost)}</span>
                    </div>
                    <div>
                      <span className="text-slate-600 block text-xs">مبلغ تعدیل</span>
                      <span className="font-mono font-bold text-amber-700">+ {formatNumber(adjustmentAmount)}</span>
                    </div>
                    <div>
                      <span className="text-slate-600 block text-xs">مبلغ با تعدیل</span>
                      <span className="font-mono font-bold text-amber-900">{formatNumber(totalWithAdjustment)}</span>
                    </div>
                    <div className="text-xs text-slate-600">
                      به مبلغ با تعدیل، با همان نسبت، مالیات و بیمه تعلق می‌گیرد (مالیات: {formatNumber(taxOnAdjusted)}، بیمه: {formatNumber(insuranceOnAdjusted)}).
                    </div>
                  </div>
                  <div className="border-t border-amber-200 pt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-slate-600 block text-xs">سود (فعلی)</span>
                      <span className="font-mono font-bold text-slate-800">{formatNumber(analysis.total_profit)}</span>
                    </div>
                    <div>
                      <span className="text-slate-600 block text-xs">کسورات از تعدیل (مالیات {formatPercent(taxDisplayPct)}٪)</span>
                      <span className="font-mono text-red-600">− {formatNumber(taxOnAdjustment)}</span>
                    </div>
                    <div>
                      <span className="text-slate-600 block text-xs">کسورات از تعدیل (بیمه {formatPercent(insuranceDisplayPct)}٪)</span>
                      <span className="font-mono text-red-600">− {formatNumber(insuranceOnAdjustment)}</span>
                    </div>
                    <div>
                      <span className="text-slate-600 block text-xs">خالص تعدیل به سود (بعد از کسر {formatPercent(totalDeductionPct)}٪ کسورات)</span>
                      <span className="font-mono font-bold text-emerald-700">+ {formatNumber(adjustmentAfterDeductions)}</span>
                    </div>
                    <div className="sm:col-span-2">
                      <span className="text-slate-600 block text-xs">سود با تعدیل</span>
                      <span className="font-mono font-bold text-emerald-800 text-lg">{formatNumber(profitWithAdjustment)}</span>
                    </div>
                  </div>
                </div>
              )}
              {analysis.has_prepayment && prepaymentAmounts.length > 0 && (
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 sm:p-4">
                  <h3 className="text-sm font-bold text-teal-900 mb-3">اقساط پیش‌پرداخت (از مبلغ فعلی — مدیریت نقدینگی)</h3>
                  <div className="flex flex-wrap gap-3 sm:gap-4">
                    {prepaymentAmounts.map((amount, idx) => (
                      <div key={idx} className="bg-white rounded-lg border border-teal-200 px-3 sm:px-4 py-2 min-w-0 sm:min-w-[140px] flex-1 basis-[calc(50%-0.375rem)] sm:basis-auto">
                        <span className="text-xs text-slate-600 block">قسط {idx + 1}</span>
                        <span className="font-mono font-bold text-teal-800">{formatNumber(effectivePrepaymentPcts[idx])}٪</span>
                        <span className="text-slate-500 mx-1">=</span>
                        <span className="font-mono font-bold text-teal-900">{formatNumber(amount)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-600 mt-2">جمع اقساط: {formatNumber(Math.ceil(prepaymentAmounts.reduce((a, b) => a + b, 0)))} ریال</p>
                </div>
              )}
              {analysis.has_maintenance_period && analysis.maintenance_retention_percentage != null && (
                <div className="bg-slate-100 border border-slate-300 rounded-xl p-4">
                  <h3 className="text-sm font-bold text-slate-900 mb-2">دوره نگهداری</h3>
                  <p className="text-sm text-slate-700 mb-2">{analysis.maintenance_duration || '—'}</p>
                  <p className="text-sm text-slate-700">درصد مبلغ بلوکه شده بعد از پیمان: {formatPercent(parseFloat(String(analysis.maintenance_retention_percentage)))}٪</p>
                  <p className="text-sm font-bold text-slate-800 mt-2">مبلغ بلوکه شده (تأثیر در برآورد): {formatNumber(Math.ceil(totalCostNum * (parseFloat(String(analysis.maintenance_retention_percentage)) || 0) / 100))} ریال</p>
                </div>
              )}
              {(analysis.extra_coefficients?.length ?? 0) > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                  <h3 className="text-sm font-bold text-indigo-900 mb-3">ضرایب / کسورات اضافی (از سود کسر می‌شود)</h3>
                  <div className="space-y-2">
                    {analysis.extra_coefficients!.map((coef, idx) => {
                      const pct = typeof coef.percentage === 'number' ? coef.percentage : parseFloat(String(coef.percentage)) || 0;
                      const amount = Math.ceil((totalCostNum * pct) / 100);
                      return (
                        <div key={idx} className="flex justify-between items-center bg-white rounded-lg border border-indigo-200 px-4 py-2">
                          <span className="font-medium text-indigo-900">{coef.name}</span>
                          <span className="text-slate-600">{formatPercent(pct)}٪</span>
                          <span className="font-mono font-bold text-indigo-800">{formatNumber(amount)} ریال</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 pt-3 border-t border-indigo-200 flex flex-wrap items-center gap-4">
                    <div>
                      <span className="text-xs text-slate-600 block">مجموع ضرایب اضافی (کسورات از سود)</span>
                      <span className="font-mono font-bold text-red-700">− {formatNumber(extraCoeffTotal)} ریال</span>
                    </div>
                    <div>
                      <span className="text-xs text-slate-600 block">سود خالص (بعد از کسر ضرایب اضافی)</span>
                      <span className="font-mono font-bold text-indigo-900">{formatNumber(profitAfterExtraCoeffs)} ریال</span>
                    </div>
                    {analysis.has_adjustment && adjustmentPct > 0 && (
                      <div>
                        <span className="text-xs text-slate-600 block">سود با تعدیل، بعد از کسر ضرایب اضافی</span>
                        <span className="font-mono font-bold text-indigo-900">{formatNumber(profitWithAdjustmentAfterExtraCoeffs)} ریال</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </details>
        ) : null}
        </>
        )}
      </div>

      {detailTab === 'insights' && analysis && (
        <AnalysisInsights analysis={analysis} />
      )}

      {detailTab === 'ai' && analysis && (
        <AnalysisAiPanel analysis={analysis} onUpdated={setAnalysis} />
      )}

      {detailTab === 'sections' && (
      <>
      {/* بخش‌های آنالیز - اسکرول طبیعی صفحه، بدون اسکرول تو در تو */}
      <div className="flex-1 px-3 lg:px-6 py-4 lg:py-5 overflow-y-auto">
        <h2 className="text-sm font-bold text-slate-600 flex items-center gap-2 mb-3">
          <Package size={18} />
          بخش‌های آنالیز
        </h2>

        {analysis.sections?.map((section) => (
          <div key={section.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow mb-3">
            <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-slate-50/50 gap-2">
              <button onClick={() => toggleSection(section.id)} className="flex-1 flex items-center justify-between text-right min-w-0 min-h-[44px] sm:min-h-0 py-1 touch-manipulation">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                    <Calculator size={18} className="text-teal-700 sm:w-5 sm:h-5" />
                  </div>
                  <div className="min-w-0">
                    <span className="font-bold text-slate-800 block truncate">{section.name}</span>
                    <span className="text-xs text-slate-500">
                      {section.items?.length ?? 0} آیتم
                      {sectionInsuranceRate(section, analysis) > 0 && <> · بیمه {sectionInsuranceRate(section, analysis)}٪</>}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                  <span className="font-bold text-teal-700 text-sm sm:text-lg sm:font-mono">
                    <span className="sm:hidden">{formatCompactMoney(section.calculated_cost)}</span>
                    <span className="hidden sm:inline">{formatNumber(section.calculated_cost)}</span>
                  </span>
                  {expandedSections.has(section.id) ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
                </div>
              </button>
              <div className="hidden lg:flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                <button type="button" onClick={() => setAddItemModalSection(section)} className="p-2 text-emerald-700 hover:bg-emerald-50 rounded-lg" title="افزودن آیتم">
                  <Plus size={16} />
                </button>
                <button type="button" onClick={() => { setModalSection(section); setEditForm({ name: section.name, description: section.description ?? '', insurance_percentage: section.insurance_percentage ?? '' }); }} className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg" title="ویرایش بخش">
                  <Pencil size={16} />
                </button>
                <button type="button" onClick={() => openCopySectionModal(section)} className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg" title="کپی بخش">
                  <Copy size={16} />
                </button>
                <button type="button" onClick={() => setDeleteSection(section)} className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg" title="حذف بخش">
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="relative shrink-0 lg:hidden" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => setSectionMenuOpenId((id) => (id === section.id ? null : section.id))}
                  className="min-w-[44px] min-h-[44px] p-2 flex items-center justify-center text-slate-500 hover:bg-slate-100 active:bg-slate-200 rounded-lg touch-manipulation"
                  title="عملیات بخش"
                >
                  <MoreVertical size={18} />
                </button>
                {sectionMenuOpenId === section.id && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setSectionMenuOpenId(null)} aria-hidden />
                    <div className="absolute right-0 sm:left-0 sm:right-auto top-full mt-1 w-52 bg-white border border-slate-200 rounded-lg shadow-xl py-1 z-50">
                      <button type="button" onClick={() => { openCopySectionModal(section); setSectionMenuOpenId(null); }} className="w-full text-right px-3 py-3 min-h-[44px] text-sm hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2 touch-manipulation">
                        <Copy size={14} /> کپی بخش به آنالیز دیگر
                      </button>
                      <button type="button" onClick={() => { setModalSection(section); setEditForm({ name: section.name, description: section.description ?? '', insurance_percentage: section.insurance_percentage ?? '' }); setSectionMenuOpenId(null); }} className="w-full text-right px-3 py-3 min-h-[44px] text-sm hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2 touch-manipulation">
                        <Pencil size={14} /> ویرایش بخش
                      </button>
                      <button type="button" onClick={() => { setDeleteSection(section); setSectionMenuOpenId(null); }} className="w-full text-right px-3 py-3 min-h-[44px] text-sm hover:bg-red-50 active:bg-red-100 text-red-600 flex items-center gap-2 touch-manipulation">
                        <Trash2 size={14} /> حذف بخش
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {expandedSections.has(section.id) && (
              <div className="border-t border-slate-100 bg-slate-50/30">
                <div className="p-2 sm:p-4 overflow-x-auto -mx-2 sm:mx-0" style={{ WebkitOverflowScrolling: 'touch' }}>
                  <table className="w-full text-sm min-w-[500px]">
                    <thead>
                      <tr className="text-right text-slate-500 text-xs border-b border-slate-200">
                        <th className="p-3 w-10">نوع</th>
                        <th className="p-3">شرح</th>
                        <th className="p-3">واحد</th>
                        <th className="p-3">مقدار</th>
                        <th className="p-3">بهای واحد</th>
                        <th className="p-3">بهای کل</th>
                        <th className="p-3 w-14" />
                      </tr>
                    </thead>
                    <tbody>
                      {section.items?.map((item) => {
                        const Icon = getItemTypeIcon(item.name);
                        return (
                          <tr key={item.id} className="group border-b border-slate-100 hover:bg-slate-50/80 transition-colors">
                            <td className="p-3"><Icon size={16} className="text-teal-600 inline" /></td>
                            <td className="p-3 font-medium text-slate-800">{item.name}</td>
                            <td className="p-3 text-slate-600">{item.unit ?? '—'}</td>
                            <td className="p-3 font-mono">{formatNumber(item.quantity)}</td>
                            <td className="p-3 font-mono">{formatNumber(item.unit_cost)}</td>
                            <td className="p-3 font-mono font-bold">{formatNumber(item.total_cost)}</td>
                            <td className="p-3">
                              <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                <button onClick={() => { setModalItem({ section, item }); setItemForm({ name: item.name, unit: item.unit ?? '', quantity: item.quantity, unit_cost: item.unit_cost, description: item.description ?? '' }); }} className="min-w-[36px] min-h-[36px] p-2 flex items-center justify-center text-slate-500 hover:bg-slate-100 active:bg-slate-200 rounded touch-manipulation" title="ویرایش"><Pencil size={14} /></button>
                                <button onClick={() => setDeleteItem({ section, item })} className="min-w-[36px] min-h-[36px] p-2 flex items-center justify-center text-red-500 hover:bg-red-50 active:bg-red-100 rounded touch-manipulation" title="حذف"><Trash2 size={14} /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-slate-200 bg-slate-100/50">
                        <td className="p-3" colSpan={2}>
                          {section.items && section.items.length > 0 && (
                            <button type="button" onClick={() => openCopyItemsModal(section)} className="text-xs text-emerald-700 font-bold hover:underline inline-flex items-center gap-1">
                              <Copy size={12} />
                              کپی آیتم‌ها به آنالیز دیگر
                            </button>
                          )}
                        </td>
                        <td colSpan={3} className="p-3 text-right text-slate-600 text-sm">جمع پایه</td>
                        <td className="p-3 font-mono font-medium text-slate-700">{formatNumber(section.base_cost)}</td>
                        <td />
                      </tr>
                      {sectionInsuranceRate(section, analysis) > 0 && (
                        <tr className="border-t border-violet-100 bg-violet-50/60">
                          <td colSpan={5} className="p-3 text-right text-violet-800 text-sm">بیمه ({sectionInsuranceRate(section, analysis)}٪ از جمع کل بخش)</td>
                          <td className="p-3 font-mono font-medium text-violet-700">{formatNumber(sectionInsuranceAmount(section, analysis))}</td>
                          <td />
                        </tr>
                      )}
                      <tr className="border-t-2 border-emerald-200 bg-emerald-50/80 font-bold">
                        <td colSpan={5} className="p-3 text-right text-emerald-800">جمع کل بخش (پایه + بیمه + سهم مالیات و سود)</td>
                        <td className="p-3 font-mono text-emerald-700">{formatNumber(section.calculated_cost)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="p-3 sm:p-4 border-t border-slate-100">
                  <button onClick={() => { setAddItemModalSection(section); setNewItem({ name: '', unit: '', quantity: '', unit_cost: '', description: '' }); }} className="inline-flex items-center gap-2 min-h-[44px] px-3 py-2 text-sm text-teal-700 font-bold hover:underline hover:bg-teal-50/50 active:bg-teal-50 rounded-lg touch-manipulation">
                    <Plus size={18} />
                    افزودن آیتم
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        <button onClick={() => setAddSectionOpen(true)} className="w-full min-h-[48px] py-4 border-2 border-dashed border-teal-400 text-teal-700 rounded-xl hover:bg-teal-50/50 active:bg-teal-50 flex items-center justify-center gap-2 font-bold transition-colors touch-manipulation">
          <Plus size={22} />
          افزودن بخش جدید
        </button>
      </div>
      </>
      )}

      {/* مودال تنظیمات خروجی */}
      <Modal
        open={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        title="تنظیمات خروجی"
        size="md"
        footer={
          <>
            <button type="button" onClick={() => setPrintModalOpen(false)} className="px-3.5 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 font-medium text-slate-700">انصراف</button>
            <button type="button" onClick={doPrintOrPdf} className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg font-bold inline-flex items-center gap-1.5 hover:bg-teal-700">
              {printAction === 'pdf' ? <FileDown size={16} /> : <Printer size={16} />}
              {printAction === 'pdf' ? 'خروجی PDF' : 'چاپ'}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">رند کردن اعداد</p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">مبالغ</label>
                <select value={printDecimalPlacesAmounts} onChange={(e) => setPrintDecimalPlacesAmounts(Number(e.target.value))} className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" dir="ltr">
                  {[0, 1, 2, 3, 4].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">مقادیر</label>
                <select value={printDecimalPlacesQuantity} onChange={(e) => setPrintDecimalPlacesQuantity(Number(e.target.value))} className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" dir="ltr">
                  {[0, 1, 2, 3, 4].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">ضریب</label>
                <select value={printDecimalPlacesCoefficient} onChange={(e) => setPrintDecimalPlacesCoefficient(Number(e.target.value))} className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" dir="ltr">
                  {[2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="space-y-2 rounded-lg border border-slate-100 bg-slate-50/60 p-2.5">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={printIncludeCharts} onChange={(e) => setPrintIncludeCharts(e.target.checked)} className="mt-0.5 w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
              <span className="text-sm text-slate-700">شامل چارت توزیع هزینه‌ها</span>
            </label>
            {analysis.has_prepayment && (analysis.prepayment_percentages?.length ?? 0) > 0 && (
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={printIncludePrepayment} onChange={(e) => setPrintIncludePrepayment(e.target.checked)} className="mt-0.5 w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                <span className="text-sm text-slate-700">نمایش اقساط پیش‌پرداخت</span>
              </label>
            )}
            {analysis.has_adjustment && analysis.adjustment_predicted_percentage != null && (
              <>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={printShowAdjustment} onChange={(e) => setPrintShowAdjustment(e.target.checked)} className="mt-0.5 w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                  <span className="text-sm text-slate-700">نمایش تعدیل در خروجی</span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={printIncludeAdjustmentInAmounts} onChange={(e) => setPrintIncludeAdjustmentInAmounts(e.target.checked)} className="mt-0.5 w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                  <span className="text-sm text-slate-700 leading-snug">اعمال تعدیل در مبالغ نهایی (سود با تعدیل)</span>
                </label>
              </>
            )}
          </div>
          <div>
            <p className="text-xs font-bold text-slate-500 mb-1.5">امضاها</p>
            <div className="space-y-1.5">
              {printSignatures.map((sig, i) => (
                <div key={i} className="grid grid-cols-2 gap-1.5">
                  <input value={sig.position} onChange={(e) => setPrintSignatures((p) => p.map((s, j) => (j === i ? { ...s, position: e.target.value } : s)))} placeholder="سمت" className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm" />
                  <input value={sig.name} onChange={(e) => setPrintSignatures((p) => p.map((s, j) => (j === i ? { ...s, name: e.target.value } : s)))} placeholder="نام" className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm" />
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">امضاهای خالی نمایش داده نمی‌شوند</p>
          </div>
        </div>
      </Modal>

      {/* مودال‌ها */}
      <Modal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        title="تنظیمات آنالیز"
        size="md"
        footer={
          <>
            <button type="button" onClick={() => setSettingsOpen(false)} className="px-3.5 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 font-medium text-slate-700">انصراف</button>
            <button type="button" onClick={handleSaveSettings} disabled={saving} className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg font-bold disabled:opacity-50 hover:bg-teal-700">ذخیره</button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="rounded-lg border border-emerald-200/80 bg-emerald-50/40 p-2.5 space-y-1.5">
            <label className="block text-xs font-bold text-emerald-900">برآورد اولیه (ریال)</label>
            <input
              type="text"
              inputMode="decimal"
              value={formatNumberInput(settings.initial_estimate)}
              onChange={(e) => setSettings((p) => ({ ...p, initial_estimate: parseNumberInput(e.target.value) }))}
              placeholder="مثلاً ۱٬۰۰۰٬۰۰۰٬۰۰۰"
              className="w-full px-3 py-2 text-sm border border-emerald-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none bg-white font-mono"
              dir="ltr"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">درصد مالیات</label>
              <input type="number" step="0.01" min="0" max="100" value={settings.tax_percentage} onChange={(e) => setSettings((p) => ({ ...p, tax_percentage: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" dir="ltr" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">نوع سود</label>
              <select value={settings.profit_type} onChange={(e) => setSettings((p) => ({ ...p, profit_type: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none">{PROFIT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
            </div>
          </div>
          <div className="rounded-lg bg-violet-50/80 border border-violet-100 px-2.5 py-2.5 space-y-2">
            <p className="text-xs font-bold text-violet-900">بیمه</p>
            <div className="grid grid-cols-2 gap-2">
              <label className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 cursor-pointer ${settings.insurance_mode === 'global' ? 'border-violet-400 bg-white' : 'border-violet-100 bg-violet-50/40'}`}>
                <input
                  type="radio"
                  name="insurance_mode"
                  checked={settings.insurance_mode === 'global'}
                  onChange={() => setSettings((p) => ({ ...p, insurance_mode: 'global' }))}
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-xs font-bold text-violet-900">کلی</span>
                  <span className="block text-[11px] text-violet-700 leading-snug">یک درصد روی جمع کل</span>
                </span>
              </label>
              <label className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 cursor-pointer ${settings.insurance_mode === 'sections' ? 'border-violet-400 bg-white' : 'border-violet-100 bg-violet-50/40'}`}>
                <input
                  type="radio"
                  name="insurance_mode"
                  checked={settings.insurance_mode === 'sections'}
                  onChange={() => setSettings((p) => ({ ...p, insurance_mode: 'sections' }))}
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-xs font-bold text-violet-900">تفکیکی</span>
                  <span className="block text-[11px] text-violet-700 leading-snug">درصد جدا برای هر بخش</span>
                </span>
              </label>
            </div>
            {settings.insurance_mode === 'global' ? (
              <div>
                <label className="block text-xs font-bold text-violet-900 mb-1">درصد بیمه از جمع کل</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={settings.insurance_percentage}
                  onChange={(e) => setSettings((p) => ({ ...p, insurance_percentage: e.target.value }))}
                  placeholder="مثلاً ۱۶"
                  className="w-full px-3 py-2 text-sm border border-violet-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none bg-white"
                  dir="ltr"
                />
                <p className="text-[11px] text-violet-700 mt-1 leading-relaxed">اگر جمع کل ۱ میلیارد شود و بیمه ۱۶٪ باشد، مبلغ بیمه ۱۶۰ میلیون است — بعد از سود.</p>
              </div>
            ) : (
              <p className="text-[11px] text-violet-700 leading-relaxed">درصد هر بخش را هنگام ویرایش همان بخش وارد کنید. سهم بیمه از جمع نهایی همان بخش است؛ اگر همه بخش‌ها ۱۰٪ باشند و جمع کل ۱ میلیارد شود، بیمه ۱۰۰ میلیون می‌شود.</p>
            )}
            <div className="flex items-center justify-between gap-2 pt-1 border-t border-violet-100">
              <p className="text-[11px] text-violet-600">موثر {formatPercent(insuranceDisplayPct)}٪ از جمع کل</p>
              <p className="font-mono text-sm font-bold text-violet-900 shrink-0">{formatNumber(analysis.total_insurance)}</p>
            </div>
          </div>
          {settings.profit_type !== 'amount' ? (
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">درصد سود</label>
              <input type="number" step="0.01" min="0" max="100" value={settings.profit_percentage} onChange={(e) => setSettings((p) => ({ ...p, profit_percentage: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" dir="ltr" />
            </div>
          ) : (
            <div className="space-y-2">
              <div className="[direction:ltr]" dir="ltr">
                <label className="block text-xs font-bold text-slate-600 mb-1">مبلغ سود</label>
                <input type="text" inputMode="decimal" value={formatNumberInput(settings.profit_amount)} onChange={(e) => setSettings((p) => ({ ...p, profit_amount: parseNumberInput(e.target.value) }))} placeholder="مبلغ ثابت" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" dir="ltr" />
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={settings.profit_amount_meaning === 'with_adjustment'} onChange={(e) => setSettings((p) => ({ ...p, profit_amount_meaning: e.target.checked ? 'with_adjustment' : 'before_adjustment' }))} className="mt-0.5 w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                <span className="text-sm text-slate-700 leading-snug">سود با تعدیل (انتظاری)</span>
              </label>
            </div>
          )}
          <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-2.5 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={settings.auto_calculate_coefficient} onChange={(e) => setSettings((p) => ({ ...p, auto_calculate_coefficient: e.target.checked }))} className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
              <span className="text-sm font-bold text-slate-700">محاسبه خودکار ضریب پیمانکار</span>
            </label>
            {!settings.auto_calculate_coefficient && (
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">ضریب هدف</label>
                <input type="number" step="0.0001" min="0.01" value={settings.target_contractor_coefficient} onChange={(e) => setSettings((p) => ({ ...p, target_contractor_coefficient: e.target.value }))} placeholder="1.0234" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" dir="ltr" />
              </div>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        open={editAnalysisOpen}
        onClose={() => setEditAnalysisOpen(false)}
        title="ویرایش آنالیز"
        size="lg"
        footer={
          <>
            <button type="button" onClick={() => setEditAnalysisOpen(false)} className="px-3.5 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 font-medium text-slate-700">انصراف</button>
            <button type="button" onClick={handleSaveEditAnalysis} disabled={saving || !editAnalysisForm.name.trim()} className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg font-bold disabled:opacity-50 hover:bg-teal-700">ذخیره</button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-slate-600 mb-1">پروژه</label>
              <SearchableSelect
                options={projectOptions}
                value={editAnalysisForm.project_id === '' ? null : editAnalysisForm.project_id}
                onChange={(v) => setEditAnalysisForm((p) => ({ ...p, project_id: v != null ? (v as number) : '' }))}
                placeholder="انتخاب پروژه"
                searchPlaceholder="جستجوی پروژه"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-slate-600 mb-1">نام آنالیز *</label>
              <input value={editAnalysisForm.name} onChange={(e) => setEditAnalysisForm((p) => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-bold text-emerald-800 mb-1">برآورد اولیه (ریال)</label>
              <input
                type="text"
                inputMode="decimal"
                value={formatNumberInput(editAnalysisForm.initial_estimate)}
                onChange={(e) => setEditAnalysisForm((p) => ({ ...p, initial_estimate: parseNumberInput(e.target.value) }))}
                placeholder="مثلاً ۱٬۰۰۰٬۰۰۰٬۰۰۰"
                className="w-full px-3 py-2 text-sm border border-emerald-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none bg-emerald-50/40 font-mono"
                dir="ltr"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">توضیحات</label>
              <input value={editAnalysisForm.description} onChange={(e) => setEditAnalysisForm((p) => ({ ...p, description: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" />
            </div>
          </div>

          <div className="rounded-lg border border-slate-100 divide-y divide-slate-100">
            <div className="p-2.5 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editAnalysisForm.has_prepayment} onChange={(e) => setEditAnalysisForm((p) => ({ ...p, has_prepayment: e.target.checked }))} className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                <span className="text-sm font-bold text-slate-700">پیش‌پرداخت</span>
              </label>
              {editAnalysisForm.has_prepayment && (
                <div className="space-y-2 pr-1">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-500 mb-1">کل پیش‌پرداخت ٪</label>
                      <input type="number" step="0.01" min={0} max={100} value={editAnalysisForm.prepayment_total_percent} onChange={(e) => setEditAnalysisForm((p) => ({ ...p, prepayment_total_percent: e.target.value }))} placeholder="۲۰" className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-teal-500/40" dir="ltr" />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-500 mb-1">تعداد اقساط</label>
                      <input
                        type="number"
                        min={1}
                        max={24}
                        value={editAnalysisForm.prepayment_installments}
                        onChange={(e) => {
                          const n = Math.max(1, Math.min(24, parseInt(e.target.value, 10) || 1));
                          const basePct = 100 / n;
                          const defaultPcts = Array.from(
                            { length: n },
                            (_, i) => (i === n - 1 ? (100 - basePct * (n - 1)).toFixed(2) : basePct.toFixed(2))
                          );
                          setEditAnalysisForm((p) => ({ ...p, prepayment_installments: n, prepayment_percentages: defaultPcts }));
                        }}
                        className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-teal-500/40"
                        dir="ltr"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                    {editAnalysisForm.prepayment_percentages.slice(0, editAnalysisForm.prepayment_installments).map((pct, idx) => (
                      <div key={idx} className="flex items-center gap-1">
                        <span className="text-[11px] text-slate-400 w-8 shrink-0">{idx + 1}</span>
                        <input
                          type="number"
                          step="0.01"
                          min={0}
                          max={100}
                          value={pct}
                          onChange={(e) => {
                            const val = e.target.value;
                            setEditAnalysisForm((p) => {
                              const arr = [...p.prepayment_percentages];
                              arr[idx] = val;
                              return { ...p, prepayment_percentages: arr };
                            });
                          }}
                          className="w-full px-2 py-1 text-sm border border-slate-200 rounded-md outline-none focus:ring-2 focus:ring-teal-500/40"
                          dir="ltr"
                        />
                        <span className="text-[11px] text-slate-400">٪</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400">
                    جمع:{' '}
                    {editAnalysisForm.prepayment_percentages
                      .slice(0, editAnalysisForm.prepayment_installments)
                      .reduce((s, p) => s + (parseFloat(p) || 0), 0)
                      .toFixed(1)}
                    ٪
                  </p>
                </div>
              )}
            </div>

            <div className="p-2.5 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editAnalysisForm.has_adjustment} onChange={(e) => setEditAnalysisForm((p) => ({ ...p, has_adjustment: e.target.checked }))} className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                <span className="text-sm font-bold text-slate-700">تعدیل</span>
              </label>
              {editAnalysisForm.has_adjustment && (
                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">پیش‌بینی درصد تعدیل</label>
                  <input type="number" step="0.01" min={0} max={100} value={editAnalysisForm.adjustment_predicted_percentage} onChange={(e) => setEditAnalysisForm((p) => ({ ...p, adjustment_predicted_percentage: e.target.value }))} placeholder="۱۵" className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-teal-500/40" dir="ltr" />
                </div>
              )}
            </div>

            <div className="p-2.5 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editAnalysisForm.has_maintenance_period} onChange={(e) => setEditAnalysisForm((p) => ({ ...p, has_maintenance_period: e.target.checked }))} className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                <span className="text-sm font-bold text-slate-700">دوره نگهداری</span>
              </label>
              {editAnalysisForm.has_maintenance_period && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] font-medium text-slate-500 mb-1">مدت</label>
                    <input type="text" value={editAnalysisForm.maintenance_duration} onChange={(e) => setEditAnalysisForm((p) => ({ ...p, maintenance_duration: e.target.value }))} placeholder="۱۲ ماه" className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-teal-500/40" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-slate-500 mb-1">٪ بلوکه</label>
                    <input type="number" step="0.01" min={0} max={100} value={editAnalysisForm.maintenance_retention_percentage} onChange={(e) => setEditAnalysisForm((p) => ({ ...p, maintenance_retention_percentage: e.target.value }))} placeholder="۵" className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-teal-500/40" dir="ltr" />
                  </div>
                </div>
              )}
            </div>

            <div className="p-2.5 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-xs font-bold text-slate-600">ضرایب / کسورات اضافی</h4>
                <button type="button" onClick={() => setEditAnalysisForm((p) => ({ ...p, extra_coefficients: [...p.extra_coefficients, { name: '', percentage: '' }] }))} className="text-xs text-teal-700 font-bold hover:underline">+ افزودن</button>
              </div>
              {editAnalysisForm.extra_coefficients.map((coef, idx) => (
                <div key={idx} className="flex gap-1.5 items-center">
                  <input value={coef.name} onChange={(e) => setEditAnalysisForm((p) => ({ ...p, extra_coefficients: p.extra_coefficients.map((c, i) => (i === idx ? { ...c, name: e.target.value } : c)) }))} placeholder="نام" className="flex-1 px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-teal-500/40" />
                  <input type="number" step="0.01" min={0} max={100} value={coef.percentage} onChange={(e) => setEditAnalysisForm((p) => ({ ...p, extra_coefficients: p.extra_coefficients.map((c, i) => (i === idx ? { ...c, percentage: e.target.value } : c)) }))} placeholder="٪" className="w-16 px-2 py-1.5 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-teal-500/40" dir="ltr" />
                  <button type="button" onClick={() => setEditAnalysisForm((p) => ({ ...p, extra_coefficients: p.extra_coefficients.filter((_, i) => i !== idx) }))} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!modalSection}
        onClose={() => setModalSection(null)}
        title="ویرایش بخش"
        size="sm"
        footer={
          <>
            <button type="button" onClick={() => setModalSection(null)} className="px-3.5 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 font-medium text-slate-700">انصراف</button>
            <button type="button" onClick={handleSaveSection} disabled={saving} className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg font-bold disabled:opacity-50 hover:bg-teal-700">ذخیره</button>
          </>
        }
      >
        <div className="space-y-2.5">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">نام بخش *</label>
            <input value={editForm.name} onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">درصد بیمه از جمع کل بخش</label>
              {analysisInsuranceMode(analysis) === 'global' ? (
                <p className="text-xs text-violet-700 leading-relaxed px-1 py-2">در حالت کلی، درصد از تنظیمات آنالیز ({analysis?.insurance_percentage ?? 0}٪) گرفته می‌شود.</p>
              ) : (
                <input type="number" step="0.01" min="0" max="100" value={editForm.insurance_percentage} onChange={(e) => setEditForm((p) => ({ ...p, insurance_percentage: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" />
              )}
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">توضیحات</label>
              <input value={editForm.description} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" />
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!addItemModalSection}
        onClose={() => { setAddItemModalSection(null); setNewItem({ name: '', unit: '', quantity: '', unit_cost: '', description: '' }); }}
        title={addItemModalSection ? `افزودن آیتم · ${addItemModalSection.name}` : 'افزودن آیتم'}
        size="md"
        footer={
          <>
            <button type="button" onClick={() => { setAddItemModalSection(null); setNewItem({ name: '', unit: '', quantity: '', unit_cost: '', description: '' }); }} className="px-3.5 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 font-medium text-slate-700">انصراف</button>
            <button type="button" onClick={handleAddItem} disabled={saving} className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg font-bold disabled:opacity-50 hover:bg-teal-700">ذخیره</button>
          </>
        }
      >
        <div className="space-y-2.5">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">شرح *</label>
            <input value={newItem.name} onChange={(e) => setNewItem((p) => ({ ...p, name: e.target.value }))} placeholder="شرح کار" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">واحد</label>
              <input value={newItem.unit} onChange={(e) => setNewItem((p) => ({ ...p, unit: e.target.value }))} placeholder="واحد" className="w-full px-2.5 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" />
            </div>
            <div className="[direction:ltr]" dir="ltr">
              <label className="block text-xs font-bold text-slate-600 mb-1">مقدار *</label>
              <input type="text" inputMode="decimal" value={formatNumberInput(newItem.quantity)} onChange={(e) => setNewItem((p) => ({ ...p, quantity: parseNumberInput(e.target.value) }))} placeholder="2.5" className="w-full px-2.5 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" dir="ltr" />
            </div>
            <div className="[direction:ltr]" dir="ltr">
              <label className="block text-xs font-bold text-slate-600 mb-1">بهای واحد *</label>
              <input type="text" inputMode="decimal" value={formatNumberInput(newItem.unit_cost)} onChange={(e) => setNewItem((p) => ({ ...p, unit_cost: parseNumberInput(e.target.value) }))} placeholder="بها" className="w-full px-2.5 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none font-mono" dir="ltr" />
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!modalItem}
        onClose={() => setModalItem(null)}
        title="ویرایش آیتم"
        size="md"
        footer={
          <>
            <button type="button" onClick={() => setModalItem(null)} className="px-3.5 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 font-medium text-slate-700">انصراف</button>
            <button type="button" onClick={handleSaveItem} disabled={saving} className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg font-bold disabled:opacity-50 hover:bg-teal-700">ذخیره</button>
          </>
        }
      >
        <div className="space-y-2.5">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">شرح *</label>
            <input value={itemForm.name} onChange={(e) => setItemForm((p) => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">واحد</label>
              <input value={itemForm.unit} onChange={(e) => setItemForm((p) => ({ ...p, unit: e.target.value }))} className="w-full px-2.5 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" />
            </div>
            <div className="[direction:ltr]" dir="ltr">
              <label className="block text-xs font-bold text-slate-600 mb-1">مقدار *</label>
              <input type="text" inputMode="decimal" value={formatNumberInput(itemForm.quantity)} onChange={(e) => setItemForm((p) => ({ ...p, quantity: parseNumberInput(e.target.value) }))} placeholder="2.5" className="w-full px-2.5 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" dir="ltr" />
            </div>
            <div className="[direction:ltr]" dir="ltr">
              <label className="block text-xs font-bold text-slate-600 mb-1">بهای واحد *</label>
              <input type="text" inputMode="decimal" value={formatNumberInput(itemForm.unit_cost)} onChange={(e) => setItemForm((p) => ({ ...p, unit_cost: parseNumberInput(e.target.value) }))} className="w-full px-2.5 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none font-mono" dir="ltr" />
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={addSectionOpen}
        onClose={() => setAddSectionOpen(false)}
        title="بخش جدید"
        size="sm"
        footer={
          <>
            <button type="button" onClick={() => setAddSectionOpen(false)} className="px-3.5 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 font-medium text-slate-700">انصراف</button>
            <button type="button" onClick={handleAddSection} disabled={saving} className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg font-bold disabled:opacity-50 hover:bg-teal-700">ذخیره</button>
          </>
        }
      >
        <div className="space-y-2.5">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">نام بخش *</label>
            <input value={newSection.name} onChange={(e) => setNewSection((p) => ({ ...p, name: e.target.value }))} placeholder="نام بخش" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">درصد بیمه از جمع کل بخش</label>
              {analysisInsuranceMode(analysis) === 'global' ? (
                <p className="text-xs text-violet-700 leading-relaxed px-1 py-2">در حالت کلی، درصد از تنظیمات آنالیز گرفته می‌شود.</p>
              ) : (
                <input type="number" step="0.01" min="0" max="100" value={newSection.insurance_percentage} onChange={(e) => setNewSection((p) => ({ ...p, insurance_percentage: e.target.value }))} placeholder="مثلاً ۷.۸" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" />
              )}
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">توضیحات</label>
              <input value={newSection.description} onChange={(e) => setNewSection((p) => ({ ...p, description: e.target.value }))} placeholder="اختیاری" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/40 outline-none" />
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={copyWholeToAnalysisOpen}
        onClose={() => { setCopyWholeToAnalysisOpen(false); setCopyWholeProjectId(''); setCopyWholeTargetAnalysisId(''); }}
        title="کپی کل آنالیز"
        size="sm"
        footer={
          <>
            <button type="button" onClick={() => { setCopyWholeToAnalysisOpen(false); setCopyWholeProjectId(''); setCopyWholeTargetAnalysisId(''); }} className="px-3.5 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 font-medium text-slate-700">انصراف</button>
            <button type="button" onClick={handleCopyWholeToAnalysis} disabled={saving || !copyWholeTargetAnalysisId || !analysis?.sections?.length} className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg font-bold disabled:opacity-50 inline-flex items-center gap-1.5 hover:bg-teal-700">
              <Copy size={15} />
              کپی
            </button>
          </>
        }
      >
        <div className="space-y-2.5">
          <p className="text-xs text-slate-500 leading-relaxed">همه بخش‌ها و آیتم‌ها به آنالیز هدف اضافه می‌شوند.</p>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">پروژه هدف *</label>
            <SearchableSelect
              options={projectOptions}
              value={copyWholeProjectId}
              onChange={(v) => setCopyWholeProjectId(v === '' ? '' : (v as number))}
              placeholder="جستجو پروژه..."
              searchPlaceholder="جستجو..."
              aria-label="انتخاب پروژه"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">آنالیز هدف *</label>
            <SearchableSelect
              options={copyWholeAnalysisOptions}
              value={copyWholeTargetAnalysisId}
              onChange={(v) => setCopyWholeTargetAnalysisId(v === '' ? '' : (v as number))}
              placeholder="ابتدا پروژه را انتخاب کنید"
              disabled={!copyWholeProjectId}
              searchPlaceholder="جستجو در آنالیزها..."
              aria-label="انتخاب آنالیز"
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={!!copySectionModal}
        onClose={() => { setCopySectionModal(null); setCopySectionProjectId(''); setCopySectionTargetId(''); }}
        title={copySectionModal ? `کپی بخش · ${copySectionModal.name}` : 'کپی بخش'}
        size="sm"
        footer={
          <>
            <button type="button" onClick={() => { setCopySectionModal(null); setCopySectionProjectId(''); setCopySectionTargetId(''); }} className="px-3.5 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 font-medium text-slate-700">انصراف</button>
            <button type="button" onClick={handleCopySection} disabled={saving || !copySectionTargetId} className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg font-bold disabled:opacity-50 inline-flex items-center gap-1.5 hover:bg-teal-700">
              <Copy size={15} />
              کپی
            </button>
          </>
        }
      >
        <div className="space-y-2.5">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">پروژه هدف *</label>
            <SearchableSelect
              options={projectOptions}
              value={copySectionProjectId}
              onChange={(v) => setCopySectionProjectId(v === '' ? '' : (v as number))}
              placeholder="جستجو پروژه..."
              aria-label="انتخاب پروژه"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">آنالیز هدف *</label>
            <SearchableSelect
              options={copySectionAnalysisOptions}
              value={copySectionTargetId}
              onChange={(v) => setCopySectionTargetId(v === '' ? '' : (v as number))}
              placeholder="ابتدا پروژه را انتخاب کنید"
              disabled={!copySectionProjectId}
              searchPlaceholder="جستجو در آنالیزها..."
              aria-label="انتخاب آنالیز"
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={!!copyItemsModal}
        onClose={() => { setCopyItemsModal(null); setCopyItemIds([]); setCopyItemsProjectId(''); setCopyItemsTargetAnalysisId(''); setCopyItemsTargetSectionId(''); }}
        title={copyItemsModal ? `کپی آیتم‌ها · ${copyItemsModal.name}` : 'کپی آیتم‌ها'}
        size="md"
        footer={
          <>
            <button type="button" onClick={() => { setCopyItemsModal(null); setCopyItemIds([]); setCopyItemsProjectId(''); setCopyItemsTargetAnalysisId(''); setCopyItemsTargetSectionId(''); }} className="px-3.5 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 font-medium text-slate-700">انصراف</button>
            <button type="button" onClick={handleCopyItems} disabled={saving || copyItemIds.length === 0 || !copyItemsTargetSectionId} className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg font-bold disabled:opacity-50 inline-flex items-center gap-1.5 hover:bg-teal-700">
              <Copy size={15} />
              کپی ({copyItemIds.length})
            </button>
          </>
        }
      >
        <div className="space-y-2.5">
          <div>
            <p className="text-xs font-bold text-slate-500 mb-1.5">انتخاب آیتم‌ها</p>
            <div className="max-h-36 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-50">
              {copyItemsModal?.items?.map((item) => (
                <label key={item.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 px-2.5 py-1.5">
                  <input type="checkbox" checked={copyItemIds.includes(item.id)} onChange={() => toggleCopyItem(item.id)} className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                  <span className="text-sm text-slate-800 flex-1 min-w-0 truncate">{item.name}</span>
                  <span className="text-[11px] text-slate-400 font-mono shrink-0">{formatNumber(item.total_cost)}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-2">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">پروژه هدف *</label>
              <SearchableSelect
                options={projectOptions}
                value={copyItemsProjectId}
                onChange={(v) => setCopyItemsProjectId(v === '' ? '' : (v as number))}
                placeholder="جستجو پروژه..."
                aria-label="انتخاب پروژه"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">آنالیز هدف *</label>
              <SearchableSelect
                options={copyItemsAnalysisOptions}
                value={copyItemsTargetAnalysisId}
                onChange={(v) => onCopyItemsTargetAnalysisChange(v === '' ? '' : (v as number))}
                placeholder="ابتدا پروژه را انتخاب کنید"
                disabled={!copyItemsProjectId}
                searchPlaceholder="جستجو در آنالیزها..."
                aria-label="انتخاب آنالیز"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">بخش هدف *</label>
              <SearchableSelect
                options={copyItemsSectionOptions}
                value={copyItemsTargetSectionId}
                onChange={(v) => setCopyItemsTargetSectionId(v === '' ? '' : (v as number))}
                placeholder="ابتدا آنالیز هدف را انتخاب کنید"
                disabled={!copyItemsTargetAnalysisId}
                searchPlaceholder="جستجو در بخش‌ها..."
                aria-label="انتخاب بخش"
              />
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmModal open={!!deleteSection} title="حذف بخش" message={deleteSection ? `آیا از حذف بخش «${deleteSection.name}» اطمینان دارید؟` : ''} onConfirm={handleDeleteSection} onCancel={() => setDeleteSection(null)} danger />
      <ConfirmModal open={!!deleteItem} title="حذف آیتم" message={deleteItem ? `آیا از حذف «${deleteItem.item.name}» اطمینان دارید؟` : ''} onConfirm={handleDeleteItem} onCancel={() => setDeleteItem(null)} danger />
    </div>
  );
};

export default AnalysisDetail;
